import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { Card, CardHeader, CardBody, Button, Row, Col, Modal, ModalBody } from "reactstrap";
import api from "../../services/api";
import useAutopilotInsights from "../../hooks/useAutopilotInsights";
import useAutopilotInsightsNF from "../../hooks/useAutopilotInsightsNF";
import useAutopilotInsightsCte from "../../hooks/useAutopilotInsightsCte";
import useAutopilotInsightsFatura from "../../hooks/useAutopilotInsightsFatura";
import ChatModal from "./ChatModal";
import XmlViewerModal from "./XmlViewerModal";
import TxtViewerModal from "./TxtViewerModal";
import PayloadViewerModal from "./PayloadViewerModal";
import autopilotMiniIcon from "../../assets/img/autopilot-mini-icon.png";
import { useCompanies } from "../../hooks/companies";
import {
  AUTOPILOT_COLORS,
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotSecondaryButtonStyle,
  autopilotTitleStyle,
} from "./styles";
import { getAutopilotHealthSummary, getAutopilotSummaryLine } from "./healthSummary";
import "./InsightsCard.css";

function renderInline(text) {
  const parts = [];
  const regex = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(<strong key={match.index}>{match[1]}</strong>);
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function taxaColor(taxa) {
  if (taxa === null || taxa === undefined) return AUTOPILOT_COLORS.statusInfo;
  return taxa >= 90
    ? AUTOPILOT_COLORS.statusSuccess
    : taxa >= 70
    ? AUTOPILOT_COLORS.statusWarning
    : AUTOPILOT_COLORS.statusDanger;
}

function TabCard({ label, data, loading, active, onClick }) {
  const taxa = data && data.taxaSucesso;
  const color = taxaColor(taxa);

  return (
    <div
      className="everlog-card-interactive everlog-card-interactive--spotlight"
      onClick={onClick}
      style={{
        "--everlog-card-interactive-surface": AUTOPILOT_COLORS.insetSurface,
        "--everlog-card-interactive-surface-active":
          `radial-gradient(circle at top left, color-mix(in srgb, ${color} 15%, transparent), transparent 48%), radial-gradient(circle at bottom right, ${AUTOPILOT_COLORS.surfacePill}, transparent 62%), ${AUTOPILOT_COLORS.insetSurfaceActive}`,
        "--everlog-card-interactive-border-active": color,
        "--everlog-card-interactive-shadow-active": "var(--everlog-card-active-glow)",
        border: "1px solid " + AUTOPILOT_COLORS.border,
        borderRadius: "14px",
        padding: "14px 16px",
        opacity: active ? 1 : 0.9,
        cursor: "pointer",
        transition: "opacity 0.2s, border 0.2s, box-shadow 0.2s, transform 0.2s",
        marginBottom: "12px",
      }}
      aria-pressed={active}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
        <p style={{
          fontSize: "0.7rem",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: active ? color : AUTOPILOT_COLORS.muted,
          margin: 0,
        }}>
          {label}
        </p>
        <span style={{ fontSize: "0.65rem", color: active ? color : AUTOPILOT_COLORS.muted, transition: "transform 0.2s", display: "inline-block", transform: active ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
      </div>

      {loading && !data ? (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", height: "38px" }}>
          <ClipLoader size={18} color={AUTOPILOT_COLORS.statusInfo} />
          <span style={{ fontSize: "0.78rem", color: AUTOPILOT_COLORS.muted }}>carregando...</span>
        </div>
      ) : data ? (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "1.8rem", fontWeight: 700, color, lineHeight: 1 }}>{taxa}%</span>
            <div style={{ flex: 1, height: "5px", borderRadius: "3px", background: AUTOPILOT_COLORS.progressTrack }}>
              <div style={{
                width: taxa + "%",
                height: "100%",
                borderRadius: "3px",
                background: color,
                transition: "width 0.6s ease",
              }} />
            </div>
          </div>
          <p style={{ fontSize: "0.72rem", color: AUTOPILOT_COLORS.muted, margin: "6px 0 0" }}>
            {data.totalSuccess} sucesso · {data.totalErrors} erros · {data.total} total
          </p>
        </>
      ) : null}
    </div>
  );
}

function SubCard({ title, children, style }) {
  return (
    <div style={{
      border: "1px solid " + AUTOPILOT_COLORS.border,
      borderRadius: "14px",
      padding: "12px 16px",
      marginBottom: "12px",
      background: AUTOPILOT_COLORS.insetSurface,
      ...style,
    }}>
      {title && (
        <>
          <p style={{
            fontSize: "0.8rem",
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: AUTOPILOT_COLORS.accent,
            margin: "0 0 0.5rem 0",
          }}>
            {title}
          </p>
          <div style={{ borderBottom: "1px solid " + AUTOPILOT_COLORS.border, marginBottom: "0.75rem" }} />
        </>
      )}
      {children}
    </div>
  );
}

function TopTable({ items, onItemClick, onItemReject, onItemExplain, rejeitandoCategoria, diagnosando }) {
  return (
    <div>
      {items.map((item, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0",
            borderBottom: i < items.length - 1 ? `1px solid ${AUTOPILOT_COLORS.dividerStrong}` : "none",
          }}
        >
          <span style={{ fontSize: "0.82rem", color: AUTOPILOT_COLORS.text, flex: 1 }}>
            {item.nome}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "0.78rem", fontWeight: 600, color: AUTOPILOT_COLORS.statusDanger, minWidth: "50px", textAlign: "right" }}>
              {item.erros} erros
            </span>
            {onItemExplain && (
              <button
                onClick={function(e) { e.stopPropagation(); onItemExplain(item); }}
                disabled={diagnosando && diagnosando.nomeArquivo === item.nome}
                style={{ background: AUTOPILOT_COLORS.purpleBg, border: `1px solid ${AUTOPILOT_COLORS.purpleBorder}`, borderRadius: "4px", color: AUTOPILOT_COLORS.purpleText, fontSize: "0.68rem", padding: "2px 7px", cursor: diagnosando && diagnosando.nomeArquivo === item.nome ? "default" : "pointer", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: "4px", opacity: diagnosando && diagnosando.nomeArquivo === item.nome ? 0.6 : 1 }}
              >
                <img src={autopilotMiniIcon} alt="" style={{ width: 13, height: 13, objectFit: "contain" }} />
                {diagnosando && diagnosando.nomeArquivo === item.nome ? "..." : "Autopilot"}
              </button>
            )}
            {onItemClick && (
              <button
                onClick={function(e) { e.stopPropagation(); onItemClick(item); }}
                title="Visualizar CTe desta categoria"
                style={{ background: "none", border: `1px solid ${AUTOPILOT_COLORS.infoButtonBorder}`, color: AUTOPILOT_COLORS.infoButtonTextSoft, borderRadius: "3px", padding: "1px 5px", fontSize: "0.68rem", cursor: "pointer", lineHeight: 1.4 }}
              >
                Visualizar
              </button>
            )}
            {onItemReject && rejeitandoCategoria && rejeitandoCategoria.nome === item.nome && rejeitandoCategoria.loading ? (
              <span style={{ color: AUTOPILOT_COLORS.textSofter, fontSize: "0.68rem" }}>Rejeitando...</span>
            ) : onItemReject ? (
              <button
                onClick={function(e) { e.stopPropagation(); onItemReject(item); }}
                title="Rejeitar todos desta categoria"
                style={{ background: "none", border: `1px solid color-mix(in srgb, ${AUTOPILOT_COLORS.statusDanger} 30%, transparent)`, color: `color-mix(in srgb, ${AUTOPILOT_COLORS.statusDanger} 70%, transparent)`, borderRadius: "3px", padding: "1px 5px", fontSize: "0.68rem", cursor: "pointer", lineHeight: 1.4 }}
              >
                Rejeitar
              </button>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function getInconsistenciaContext(inconsistencia) {
  if (!inconsistencia) return '';
  if (
    inconsistencia.indexOf('Não foram encontradas Nota') !== -1 ||
    inconsistencia === 'TAG Nota não encontrada'
  ) {
    return '\n\nCONTEXTO DO SISTEMA: No TMS Everlog, um CTe só pode ser integrado quando todas as NFe que ele referencia já estão cadastradas previamente no sistema. Esta inconsistência indica que alguma NFe ainda não foi integrada — não é problema de emissão fiscal nem da SEFAZ. A causa mais provável é que a integração de NFe do embarcador ainda não enviou a nota, ou houve falha durante a integração. Oriente verificar os logs no Grafana pela chave da NFe e, se necessário, acionar o time de TI do embarcador para reenvio ou reprocessamento da NFe.';
  }
  if (inconsistencia.indexOf('não encontrada no TMS') !== -1 || inconsistencia === 'NF não encontrada no TMS') {
    return '\n\nCONTEXTO DO SISTEMA: A NFe não foi recebida ou integrada no TMS. Isso indica falha ou atraso na integração de NFe do embarcador, não um problema fiscal ou de SEFAZ.';
  }
  if (inconsistencia.indexOf('CNPJ do Remetente') !== -1 || inconsistencia === 'CNPJ do Remetente não cadastrado') {
    return '\n\nCONTEXTO DO SISTEMA: O CNPJ do remetente informado no CTe não está cadastrado no TMS. É necessário cadastrar o remetente antes de processar este CTe.';
  }
  if (inconsistencia.indexOf('CNPJ da Transportadora') !== -1 || inconsistencia === 'CNPJ da Transportadora não cadastrada') {
    return '\n\nCONTEXTO DO SISTEMA: A transportadora informada no CTe não está cadastrada no TMS. É necessário realizar o cadastro da transportadora para que o CTe seja integrado.';
  }
  return '';
}

// Espelha normalizeFaturaInconsistencia do backend (AutopilotService.js).
// Mantém os 4 buckets canônicos + Outros para o filtro do modal.
const FATURA_CATEGORIAS_FRONT = [
  'CT-e não encontrado no sistema',
  'CNPJ do Remetente não cadastrado',
  'CNPJ da Transportadora não cadastrada',
  'Arquivo já importado',
  'Outros',
];

function classifyFaturaInconsistencia(texto) {
  if (!texto) return 'Outros';
  if (texto.indexOf('não existe no Sistema') !== -1) return 'CT-e não encontrado no sistema';
  if (texto.indexOf('CNPJ do Remetente') !== -1) return 'CNPJ do Remetente não cadastrado';
  if (texto.indexOf('CNPJ da Transportadora') !== -1) return 'CNPJ da Transportadora não cadastrada';
  if (texto.indexOf('já foi importado') !== -1) return 'Arquivo já importado';
  return 'Outros';
}

function AnaliseIA({ text }) {
  return (
    <div style={{ fontSize: "0.85rem", lineHeight: "1.65", color: AUTOPILOT_COLORS.text }}>
      {text.split("\n").filter(l => l.trim()).map((line, i) => (
        <p key={i} style={{ margin: "0 0 0.5rem 0" }}>{renderInline(line)}</p>
      ))}
    </div>
  );
}

function semaphoreColor(iso) {
  if (!iso) return AUTOPILOT_COLORS.textSofter;
  const diffH = (Date.now() - new Date(iso).getTime()) / 3600000;
  if (diffH < 4) return AUTOPILOT_COLORS.statusSuccess;
  if (diffH < 24) return AUTOPILOT_COLORS.statusWarning;
  return AUTOPILOT_COLORS.statusDanger;
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "agora";
  if (diff < 3600) return "há " + Math.floor(diff / 60) + " min";
  if (diff < 86400) return "há " + Math.floor(diff / 3600) + "h";
  const days = Math.floor(diff / 86400);
  if (days < 30) return "há " + days + (days === 1 ? " dia" : " dias");
  const months = Math.floor(days / 30);
  if (months < 12) return "há " + months + (months === 1 ? " mês" : " meses");
  const years = Math.floor(months / 12);
  return "há " + years + (years === 1 ? " ano" : " anos");
}

const InsightsCard = ({ geralHook }) => {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState(null);
  const [modal, setModal] = useState({ open: false, title: "", data: [], loading: false, tipo: "backlog" });
  const [rejeitandoCategoria, setRejeitandoCategoria] = useState(null); // { nome, loading }
  const [rejeitandoIndividual, setRejeitandoIndividual] = useState(null); // { nomeArquivo, loading }
  const [rejeitarModal, setRejeitarModal] = useState({ open: false, tipo: null, payload: null, comentario: "" }); // modal de confirmação com comentário
  const [chatModal, setChatModal] = useState({ open: false, categoria: null, count: 0, initialMessage: null, title: null, preloadedAnswer: null });
  const [freteDetailsModal, setFreteDetailsModal] = useState({ open: false, title: "", data: [], loading: false });
  const [diagnosando, setDiagnosando] = useState(null);
  const [integrandoCte, setIntegrandoCte] = useState(false);
  const [integracaoFeedback, setIntegracaoFeedback] = useState(null);
  const [sftpChatLoading, setSftpChatLoading] = useState(false);
  const [xmlModal, setXmlModal] = useState({ open: false, nomeArquivo: null, idTransportadora: null });
  const [txtModal, setTxtModal] = useState({ open: false, nomeArquivo: null, idTransportadora: null });
  const [payloadModal, setPayloadModal] = useState({ open: false, chaveacesso: null, numero: null });
  const [nfModal, setNfModal] = useState({ open: false, title: "", errorMessage: null, data: [], loading: false });
  const [reprocessandoNF, setReprocessandoNF] = useState(null); // chaveacesso da NF em reprocesso individual
  const [reprocessandoTodasNF, setReprocessandoTodasNF] = useState(false);
  const [nfReprocessFeedback, setNfReprocessFeedback] = useState(null); // { ok, total, okCount, failCount }
  const [modalSearch, setModalSearch] = useState("");
  const [modalFiltroTransportadora, setModalFiltroTransportadora] = useState("");
  const [modalFiltroInconsistencia, setModalFiltroInconsistencia] = useState("");
  const [expandedCteRows, setExpandedCteRows] = useState(new Set());
  const [rejeitarPorData, setRejeitarPorData] = useState({ dataLimite: "", loading: false, confirmando: false });
  const { empresaId } = useCompanies();
  const freteHook = useAutopilotInsights();
  const nfHook = useAutopilotInsightsNF();
  const cteHook = useAutopilotInsightsCte();
  const faturaHook = useAutopilotInsightsFatura();
  const activeHook = activeTab === "frete" ? freteHook : activeTab === "nf" ? nfHook : activeTab === "cte" ? cteHook : activeTab === "fatura" ? faturaHook : geralHook;

  function handleTabClick(tab) {
    setActiveTab(function(current) { return current === tab ? null : tab; });
  }

  function openCteDetails(item) {
    const title = item.label || item.nome;
    const categoria = item.nome;
    const tipo = categoria === "all" ? "backlog" : "inconsistencia";
    setModalSearch("");
    setModalFiltroTransportadora("");
    setModalFiltroInconsistencia("");
    setExpandedCteRows(new Set());
    setModal({ open: true, title, data: [], loading: true, tipo, source: "cte" });
    const cteParams = { categoria };
    if (empresaId) cteParams.embarcadorId = empresaId;
    api.get("/autopilot/insights/cte/details", { params: cteParams })
      .then(function(res) { setModal(function(m) { return Object.assign({}, m, { data: res.data, loading: false }); }); })
      .catch(function() { setModal(function(m) { return Object.assign({}, m, { loading: false }); }); });
  }

  function openFaturaDetails(item) {
    const title = item.label || item.nome;
    const categoria = item.nome;
    const tipo = categoria === "all" ? "backlog" : "inconsistencia";
    setModalSearch("");
    setModalFiltroTransportadora("");
    setModalFiltroInconsistencia(categoria && categoria !== "all" ? categoria : "");
    setExpandedCteRows(new Set());
    setModal({ open: true, title, data: [], loading: true, tipo, source: "fatura" });
    const faturaParams = { categoria };
    if (empresaId) faturaParams.embarcadorId = empresaId;
    api.get("/autopilot/insights/fatura/details", { params: faturaParams })
      .then(function(res) { setModal(function(m) { return Object.assign({}, m, { data: res.data, loading: false }); }); })
      .catch(function() { setModal(function(m) { return Object.assign({}, m, { loading: false }); }); });
  }

  function openFreteDetails({ tipo, transportadora, rota, title }) {
    setFreteDetailsModal({ open: true, title: title || "Erros de Cálculo de Frete", data: [], loading: true });
    const params = {};
    if (empresaId) params.embarcadorId = empresaId;
    if (tipo) params.tipo = tipo;
    if (transportadora) params.transportadora = transportadora;
    if (rota) params.rota = rota;
    api.get("/autopilot/insights/frete/details", { params })
      .then(function(res) { setFreteDetailsModal(function(m) { return Object.assign({}, m, { data: res.data, loading: false }); }); })
      .catch(function() { setFreteDetailsModal(function(m) { return Object.assign({}, m, { loading: false }); }); });
  }

  function openFreteChatModal({ tipo, nome, erros }) {
    let message;
    if (tipo === "cte") {
      message = `Houve ${erros} erros de cálculo de frete em CTe nas últimas 24h. Quais são as causas mais comuns e como posso resolver?`;
    } else if (tipo === "nfe") {
      message = `Houve ${erros} erros de cálculo de frete em NFe nas últimas 24h. Quais são as causas mais comuns e como posso resolver?`;
    } else if (tipo === "transportadora") {
      message = `A transportadora "${nome}" teve ${erros} erros de cálculo de frete nas últimas 24h. O que pode estar causando esses erros e como resolver?`;
    } else if (tipo === "rota") {
      message = `A rota "${nome}" teve ${erros} erros de cálculo de frete nas últimas 24h. Qual pode ser a causa e como resolver?`;
    } else {
      message = `Houve ${erros} erros no cálculo de frete nas últimas 24h. O que pode estar causando isso?`;
    }
    setChatModal({ open: true, categoria: null, count: 0, initialMessage: message, title: "Autopilot — Cálculo de Frete" });
  }

  function closeModal() {
    setModalSearch("");
    setModalFiltroTransportadora("");
    setModalFiltroInconsistencia("");
    setExpandedCteRows(new Set());
    setRejeitarPorData({ dataLimite: "", loading: false, confirmando: false });
    setModal(function(m) { return Object.assign({}, m, { open: false }); });
  }

  function openNFDetails(item) {
    const errorMessage = item.nome;
    const title = errorMessage;
    setNfModal({ open: true, title, errorMessage, data: [], loading: true });
    const params = { errorMessage };
    if (empresaId) params.embarcadorId = empresaId;
    api.get("/autopilot/insights/nf/details", { params })
      .then(function(res) { setNfModal(function(m) { return Object.assign({}, m, { data: res.data, loading: false }); }); })
      .catch(function() { setNfModal(function(m) { return Object.assign({}, m, { loading: false }); }); });
  }

  function reprocessarNF(chaveacesso) {
    setReprocessandoNF(chaveacesso);
    const body = { chaveacesso };
    if (empresaId) body.embarcadorId = empresaId;
    api.post("/autopilot/insights/nf/reprocessar", body)
      .then(function(res) {
        setReprocessandoNF(null);
        if (res.data && res.data.ok) {
          // Remove a linha do modal — a NF foi reprocessada com sucesso
          setNfModal(function(m) { return Object.assign({}, m, { data: m.data.filter(function(r) { return r.chaveacesso !== chaveacesso; }) }); });
          setTimeout(function() { nfHook.refresh(true); }, 2000);
        } else {
          window.alert("Falha ao reprocessar: " + ((res.data && res.data.message) || "erro desconhecido"));
        }
      })
      .catch(function(err) {
        setReprocessandoNF(null);
        const detail = (err.response && err.response.data && (err.response.data.message || err.response.data.error)) || err.message;
        window.alert("Erro ao reprocessar: " + detail);
      });
  }

  function reprocessarTodasNF(errorMessage) {
    if (reprocessandoTodasNF) return;
    setReprocessandoTodasNF(true);
    setNfReprocessFeedback(null);
    const body = {};
    if (empresaId) body.embarcadorId = empresaId;
    if (errorMessage) body.errorMessage = errorMessage;
    api.post("/autopilot/insights/nf/reprocessar-todos", body)
      .then(function(res) {
        setReprocessandoTodasNF(false);
        const summary = res.data || { total: 0, ok: 0, fail: 0 };
        setNfReprocessFeedback({ ok: summary.fail === 0, total: summary.total, okCount: summary.ok, failCount: summary.fail });
        setTimeout(function() { setNfReprocessFeedback(null); nfHook.refresh(true); }, 8000);
        if (errorMessage) {
          // Drilldown aberto — atualiza lista
          setNfModal(function(m) {
            if (!m.open) return m;
            const okChaves = new Set((summary.results || []).filter(function(r) { return r.ok; }).map(function(r) { return r.chaveacesso; }));
            return Object.assign({}, m, { data: m.data.filter(function(r) { return !okChaves.has(r.chaveacesso); }) });
          });
        }
      })
      .catch(function(err) {
        setReprocessandoTodasNF(false);
        const detail = (err.response && err.response.data && (err.response.data.message || err.response.data.error)) || err.message;
        setNfReprocessFeedback({ ok: false, total: 0, okCount: 0, failCount: 0, error: detail });
        setTimeout(function() { setNfReprocessFeedback(null); }, 8000);
      });
  }

  function diagnoseNFCategoria(item) {
    const userQuestion = `Há ${item.erros} NFe que falharam na integração com o erro "${item.nome}". O que significa isso e como posso resolver?`;
    setDiagnosando({ nomeArquivo: '__nf-categoria__:' + item.nome });
    const body = { errorMessage: item.nome, count: item.erros };
    if (empresaId) body.embarcadorId = empresaId;
    api.post("/autopilot/diagnostico/nf-erro-categoria", body)
      .then(function(res) {
        setDiagnosando(null);
        setChatModal({ open: true, categoria: null, count: 0, initialMessage: null, title: item.nome, preloadedAnswer: res.data.answer || null, preloadedUserMessage: userQuestion });
      })
      .catch(function() {
        setDiagnosando(null);
        setChatModal({ open: true, categoria: item.nome, count: item.erros, initialMessage: null, title: item.nome, preloadedAnswer: null, preloadedUserMessage: null });
      });
  }

  function handleRejeitarPorData() {
    if (!rejeitarPorData.dataLimite) return;
    if (!rejeitarPorData.confirmando) {
      setRejeitarPorData(function(s) { return Object.assign({}, s, { confirmando: true }); });
      return;
    }
    const endpoint = modal.source === "fatura"
      ? "/autopilot/insights/fatura/rejeitar-por-data"
      : "/autopilot/insights/cte/rejeitar-por-data";
    const body = { dataLimite: rejeitarPorData.dataLimite };
    if (empresaId) body.embarcadorId = empresaId;
    setRejeitarPorData(function(s) { return Object.assign({}, s, { loading: true, confirmando: false }); });
    api.post(endpoint, body)
      .then(function(res) {
        setRejeitarPorData({ dataLimite: "", loading: false, confirmando: false });
        if (modal.source === "fatura") {
          setModal(function(m) { return Object.assign({}, m, { data: m.data.filter(function(r) { return !r.Dat_Emissao || new Date(r.Dat_Emissao) > new Date(rejeitarPorData.dataLimite); }) }); });
          faturaHook && faturaHook.refresh && faturaHook.refresh(true);
        } else {
          setModal(function(m) { return Object.assign({}, m, { data: m.data.filter(function(r) { return !r.Dat_Emissao || new Date(r.Dat_Emissao) > new Date(rejeitarPorData.dataLimite); }) }); });
          cteHook.refresh(true);
        }
      })
      .catch(function(err) {
        console.error("[Autopilot] Erro ao rejeitar por data:", err);
        setRejeitarPorData(function(s) { return Object.assign({}, s, { loading: false, confirmando: false }); });
      });
  }

  function handleRejeitarFaturaCategoria(item) {
    setRejeitarModal({ open: true, tipo: "fatura-categoria", payload: item, comentario: "" });
  }

  function handleRejeitarCategoria(item) {
    setRejeitarModal({ open: true, tipo: "cte-categoria", payload: item, comentario: "" });
  }

  function handleDownload(row) {
    const dlParams = { nomeArquivo: row.NomeArquivo, idTransportadora: row.IdTransportadora, download: "1" };
    if (empresaId) dlParams.embarcadorId = empresaId;
    api.get("/autopilot/insights/cte/xml", {
      params: dlParams,
      responseType: "blob",
    }).then(function(res) {
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = row.NomeArquivo; a.click();
      URL.revokeObjectURL(url);
    }).catch(function(err) {
      console.error("[Autopilot] Erro ao baixar XML:", err);
    });
  }

  function confirmarRejeitarIndividual(row) {
    const tipo = row._isFatura ? "fatura-individual" : "cte-individual";
    setRejeitarModal({ open: true, tipo, payload: row, comentario: "" });
  }

  function executarRejeicao(comentario) {
    const { tipo, payload } = rejeitarModal;
    setRejeitarModal({ open: false, tipo: null, payload: null, comentario: "" });

    const bodyBase = comentario ? { comentario } : {};
    if (empresaId) bodyBase.embarcadorId = empresaId;

    if (tipo === "cte-categoria") {
      setRejeitandoCategoria({ nome: payload.nome, loading: true });
      api.post("/autopilot/insights/cte/rejeitar", Object.assign({}, bodyBase, { categoria: payload.nome }))
        .then(function() { setRejeitandoCategoria(null); cteHook.refresh(true); })
        .catch(function(err) { console.error("[Autopilot] Erro ao rejeitar categoria:", err); setRejeitandoCategoria(null); });
    } else if (tipo === "cte-individual") {
      setRejeitandoIndividual({ nomeArquivo: payload.NomeArquivo, loading: true });
      api.post("/autopilot/insights/cte/rejeitar-individual", Object.assign({}, bodyBase, { nomeArquivo: payload.NomeArquivo }))
        .then(function() {
          setRejeitandoIndividual(null);
          setModal(function(m) { return Object.assign({}, m, { data: m.data.filter(function(r) { return r.NomeArquivo !== payload.NomeArquivo; }) }); });
          cteHook.refresh(true);
        })
        .catch(function(err) { console.error("[Autopilot] Erro ao rejeitar individual:", err); setRejeitandoIndividual(null); });
    } else if (tipo === "fatura-categoria") {
      setRejeitandoCategoria({ nome: payload.nome, loading: true });
      api.post("/autopilot/insights/fatura/rejeitar", Object.assign({}, bodyBase, { categoria: payload.nome }))
        .then(function() { setRejeitandoCategoria(null); faturaHook.refresh(true); })
        .catch(function(err) { console.error("[Autopilot] Erro ao rejeitar fatura categoria:", err); setRejeitandoCategoria(null); });
    } else if (tipo === "fatura-individual") {
      setRejeitandoIndividual({ nomeArquivo: payload.NomeArquivo, loading: true });
      api.post("/autopilot/insights/fatura/rejeitar-individual", Object.assign({}, bodyBase, { id: payload.Id }))
        .then(function() {
          setRejeitandoIndividual(null);
          setModal(function(m) { return Object.assign({}, m, { data: m.data.filter(function(r) { return r.NomeArquivo !== payload.NomeArquivo; }) }); });
          faturaHook.refresh(true);
        })
        .catch(function(err) { console.error("[Autopilot] Erro ao rejeitar fatura individual:", err); setRejeitandoIndividual(null); });
    }
  }

  // Filtros aplicados na lista do modal (busca livre + transportadora + inconsistência).
  // Inconsistência só é considerada quando source === "fatura", já que o modal de
  // CTe não expõe esse filtro hoje.
  function passesModalFilters(row) {
    if (modalSearch) {
      const term = modalSearch.toLowerCase();
      const matchSearch = (
        (row.NomeArquivo && row.NomeArquivo.toLowerCase().includes(term)) ||
        (row.Numero && String(row.Numero).toLowerCase().includes(term)) ||
        (row.Fantasia && row.Fantasia.toLowerCase().includes(term))
      );
      if (!matchSearch) return false;
    }
    if (modalFiltroTransportadora && row.Fantasia !== modalFiltroTransportadora) {
      return false;
    }
    if (modal.source === "fatura" && modalFiltroInconsistencia) {
      if (classifyFaturaInconsistencia(row.Inconsistencias) !== modalFiltroInconsistencia) {
        return false;
      }
    }
    return true;
  }

  const transportadorasDoModal = Array.from(
    new Set((modal.data || []).map(function(r) { return r.Fantasia; }).filter(Boolean))
  ).sort();

  return (
    <React.Fragment>
    <Card style={{ ...autopilotCardStyle, display: "flex", flexDirection: "column", flex: 1, background: "var(--everlog-card-hero-bg)" }}>
      <CardHeader style={autopilotHeaderStyle}>
        <Row className="align-items-start">
          <Col>
            <div style={autopilotEyebrowStyle}>Sustentação</div>
            <div style={{ ...autopilotTitleStyle, marginBottom: "0.3rem" }}>
              Monitor de integrações
            </div>
            {activeHook.data && activeHook.data.generatedAt && (
              <small style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.72rem" }}>
                {activeHook.data.fromCache ? "cache · " : ""}
                atualizado {timeAgo(activeHook.data.generatedAt)} · {activeHook.data.periodo}
              </small>
            )}
          </Col>
          <Col xs="auto">
            <Button className="btn-sm" style={autopilotSecondaryButtonStyle} onClick={activeHook.refresh} disabled={activeHook.loading}>
              {activeHook.loading ? <ClipLoader size={12} color={AUTOPILOT_COLORS.onAccent} /> : "↺ Atualizar"}
            </Button>
          </Col>
        </Row>
      </CardHeader>

      <CardBody style={{ ...autopilotBodyStyle, flex: 1 }}>
        {/* Status Geral */}
        {(function() {
          const d = geralHook.data;
          const {
            taxa: healthTaxa,
            statusColor,
            statusLabel,
          } = getAutopilotHealthSummary(d);
          const firstLine = getAutopilotSummaryLine(d && d.answer);
          const healthTaxaText = healthTaxa === null
            ? null
            : (Number.isInteger(healthTaxa) ? `${healthTaxa}%` : `${healthTaxa.toFixed(1)}%`);

          return (
            <div style={{
              border: "1px solid " + AUTOPILOT_COLORS.border,
              borderRadius: "14px",
              padding: "12px 16px",
              marginBottom: "16px",
              background: AUTOPILOT_COLORS.insetSurface,
              borderLeft: "3px solid " + statusColor,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", alignItems: "flex-start", marginBottom: firstLine ? "6px" : 0 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {geralHook.loading && !d ? (
                      <ClipLoader size={12} color={AUTOPILOT_COLORS.statusInfo} />
                    ) : (
                      <span style={{ color: statusColor, fontSize: "10px", lineHeight: 1 }}>⏺</span>
                    )}
                    <span style={{ fontSize: "0.84rem", fontWeight: 600, color: statusColor }}>
                      {geralHook.loading && !d ? "Analisando sustentação..." : statusLabel}
                    </span>
                  </div>
                </div>

                {healthTaxaText && (
                  <div style={{ color: statusColor, fontSize: "1.15rem", fontWeight: 700, lineHeight: 1 }}>
                    {healthTaxaText}
                  </div>
                )}
              </div>
              {firstLine && (
                <p style={{ fontSize: "0.8rem", color: AUTOPILOT_COLORS.muted, margin: 0, lineHeight: 1.5 }}>
                  {firstLine}
                </p>
              )}
            </div>
          );
        })()}

        <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.74rem", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "0.6rem" }}>
          Clique nos cards para visualizar os detalhes
        </div>

        {/* Tab cards — sempre visíveis, clique abre/fecha detalhes */}
        <Row>
          <Col md="3">
            <TabCard
              label="Cálculo"
              data={freteHook.data}
              loading={freteHook.loading}
              active={activeTab === "frete"}
              onClick={() => handleTabClick("frete")}
            />
          </Col>
          <Col md="3">
            <TabCard
              label="Nota Fiscal"
              data={nfHook.data}
              loading={nfHook.loading}
              active={activeTab === "nf"}
              onClick={() => handleTabClick("nf")}
            />
          </Col>
          <Col md="3">
            <TabCard
              label="CTe"
              data={cteHook.data}
              loading={cteHook.loading}
              active={activeTab === "cte"}
              onClick={() => handleTabClick("cte")}
            />
          </Col>
          <Col md="3">
            <TabCard
              label="Faturas"
              data={faturaHook.data}
              loading={faturaHook.loading}
              active={activeTab === "fatura"}
              onClick={() => handleTabClick("fatura")}
            />
          </Col>
        </Row>

        {/* Detalhes do módulo ativo — só aparece quando um tab está selecionado */}
        {activeTab !== null && activeHook.loading && !activeHook.data && (
          <div className="text-center py-3">
            <ClipLoader size={24} color={AUTOPILOT_COLORS.statusInfo} />
            <p className="mt-2 text-muted" style={{ fontSize: "0.82rem" }}>Consultando logs...</p>
          </div>
        )}

        {activeTab !== null && activeHook.error && (
          <p style={{ color: AUTOPILOT_COLORS.statusDanger, fontSize: "0.85rem" }}>{activeHook.error}</p>
        )}

        {/* Cálculo de Frete */}
        {activeTab === "frete" && freteHook.data && (
          <>
            <SubCard title="Erros por Tipo">
              <div style={{ fontSize: "0.82rem" }}>
                {[
                  { label: "CTe", tipo: "cte", count: (freteHook.data.endpointErrors && freteHook.data.endpointErrors.calcFreteCTe) || 0 },
                  { label: "NFe", tipo: "nfe", count: (freteHook.data.endpointErrors && freteHook.data.endpointErrors.calcFreteNFe) || 0 },
                ].map(function(row) {
                  return (
                    <div key={row.tipo} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: row.tipo === "cte" ? `1px solid ${AUTOPILOT_COLORS.divider}` : "none" }}>
                      <span style={{ color: AUTOPILOT_COLORS.textSoft }}>{row.label}</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ color: row.count > 0 ? AUTOPILOT_COLORS.statusDanger : AUTOPILOT_COLORS.statusSuccess, fontWeight: 600, minWidth: 50, textAlign: "right" }}>
                          {row.count} erros
                        </span>
                        {row.count > 0 && (
                          <>
                            <button
                              onClick={function() { openFreteDetails({ tipo: row.tipo, title: "Erros de Frete — " + row.label }); }}
                              style={{ background: "none", border: `1px solid ${AUTOPILOT_COLORS.infoButtonBorder}`, color: AUTOPILOT_COLORS.infoButtonTextSoft, borderRadius: "3px", padding: "1px 5px", fontSize: "0.68rem", cursor: "pointer", lineHeight: 1.4 }}
                            >
                              Visualizar
                            </button>
                            <button
                              onClick={function() { openFreteChatModal({ tipo: row.tipo, erros: row.count }); }}
                              style={{ background: AUTOPILOT_COLORS.purpleBg, border: `1px solid ${AUTOPILOT_COLORS.purpleBorder}`, borderRadius: "4px", color: AUTOPILOT_COLORS.purpleText, fontSize: "0.68rem", padding: "2px 7px", cursor: "pointer", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: "4px" }}
                            >
                              <img src={autopilotMiniIcon} alt="" style={{ width: 13, height: 13, objectFit: "contain" }} />
                              Autopilot
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </SubCard>

            {freteHook.data.topTransportadoras && freteHook.data.topTransportadoras.length > 0 && (
              <SubCard title="Top Transportadoras com Erro">
                <TopTable
                  items={freteHook.data.topTransportadoras}
                  onItemClick={function(item) { openFreteDetails({ transportadora: item.nome, title: "Erros — " + item.nome }); }}
                  onItemExplain={function(item) { openFreteChatModal({ tipo: "transportadora", nome: item.nome, erros: item.erros }); }}
                />
              </SubCard>
            )}

            {freteHook.data.topRotas && freteHook.data.topRotas.length > 0 && (
              <SubCard title="Top Rotas com Erro">
                <TopTable
                  items={freteHook.data.topRotas}
                  onItemClick={function(item) { openFreteDetails({ rota: item.nome, title: "Erros — " + item.nome }); }}
                  onItemExplain={function(item) { openFreteChatModal({ tipo: "rota", nome: item.nome, erros: item.erros }); }}
                />
              </SubCard>
            )}

            {freteHook.data.answer && (
              <SubCard title="Análise">
                <AnaliseIA text={freteHook.data.answer} />
              </SubCard>
            )}
          </>
        )}

        {/* CTe */}
        {activeTab === "cte" && cteHook.data && (
          <>
            <SubCard title="Backlog Ativo">
              <div style={{ fontSize: "0.82rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: `1px solid ${AUTOPILOT_COLORS.divider}` }}>
                  <span style={{ color: AUTOPILOT_COLORS.textSoft }}>CTe com inconsistência</span>
                  <span
                    onClick={function() { openCteDetails({ nome: "all", label: "Todos" }); }}
                    style={{ color: AUTOPILOT_COLORS.statusDanger, fontWeight: 600, cursor: "pointer" }}
                    title="Ver detalhes"
                  >
                    {cteHook.data.pendentes} <span style={{ fontSize: "0.7rem", opacity: 0.5 }}>↗</span>
                  </span>
                </div>
                {cteHook.data.maisAntigo && (
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
                    <span style={{ color: AUTOPILOT_COLORS.textSoft }}>Emissão mais antiga</span>
                    <span style={{ fontWeight: 600, color: semaphoreColor(cteHook.data.maisAntigo) }}>
                      {timeAgo(cteHook.data.maisAntigo)}
                    </span>
                  </div>
                )}
                {cteHook.data.maisRecente && (
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
                    <span style={{ color: AUTOPILOT_COLORS.textSoft }}>Emissão mais recente</span>
                    <span style={{ color: AUTOPILOT_COLORS.text }}>
                      {timeAgo(cteHook.data.maisRecente)}
                    </span>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px", marginTop: "10px" }}>
                <button
                  onClick={function() {
                    setDiagnosando({ nomeArquivo: "__backlog__" });
                    api.post("/autopilot/diagnostico/backlog-geral")
                      .then(function(res) {
                        setDiagnosando(null);
                        setChatModal({ open: true, categoria: null, count: cteHook.data.pendentes, initialMessage: null, title: "Backlog Ativo", preloadedAnswer: res.data.answer || null, preloadedUserMessage: null });
                      })
                      .catch(function() { setDiagnosando(null); });
                  }}
                  disabled={!!(diagnosando && diagnosando.nomeArquivo === "__backlog__")}
                  style={{ background: AUTOPILOT_COLORS.purpleBg, border: `1px solid ${AUTOPILOT_COLORS.purpleBorder}`, borderRadius: "4px", color: AUTOPILOT_COLORS.purpleText, fontSize: "0.68rem", padding: "2px 7px", cursor: diagnosando && diagnosando.nomeArquivo === "__backlog__" ? "not-allowed" : "pointer", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: "4px" }}
                >
                  <img src={autopilotMiniIcon} alt="" style={{ width: 13, height: 13, objectFit: "contain" }} />
                  {diagnosando && diagnosando.nomeArquivo === "__backlog__" ? "..." : "Autopilot"}
                </button>
                <button
                  onClick={function() { openCteDetails({ nome: "all", label: "Todos" }); }}
                  style={{ background: "none", border: `1px solid ${AUTOPILOT_COLORS.infoButtonBorder}`, color: AUTOPILOT_COLORS.infoButtonTextSoft, borderRadius: "3px", padding: "1px 5px", fontSize: "0.68rem", cursor: "pointer", lineHeight: 1.4 }}
                >
                  Visualizar
                </button>
              </div>
            </SubCard>

            {/* Arquivos no SFTP aguardando simulação */}
            {cteHook.data.sftpTotal > 0 && (
              <SubCard title="Aguardando Simulação (SFTP)">
                <div style={{ fontSize: "0.82rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: `1px solid ${AUTOPILOT_COLORS.divider}`, marginBottom: "6px" }}>
                    <span style={{ color: AUTOPILOT_COLORS.textSoft }}>Total de arquivos</span>
                    <span style={{ color: AUTOPILOT_COLORS.statusWarning, fontWeight: 600 }}>{cteHook.data.sftpTotal}</span>
                  </div>
                  {cteHook.data.sftpPending.map(function(item, i) {
                    return (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                        <span style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem" }}>{item.transportadora}</span>
                        <span style={{ color: AUTOPILOT_COLORS.statusWarning, fontWeight: 600, fontSize: "0.78rem" }}>{item.pendentes}</span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px", marginTop: "10px" }}>
                  <button
                    onClick={async function() {
                      setSftpChatLoading(true);
                      try {
                        const res = await api.post('/autopilot/insights/cte/sftp-chat-init', {
                          sftpTotal: cteHook.data.sftpTotal,
                          sftpPending: cteHook.data.sftpPending,
                          ...(empresaId ? { embarcadorId: empresaId } : {}),
                        });
                        setChatModal({ open: true, categoria: null, count: 0, initialMessage: null, displayMessage: null, title: "Aguardando Processamento", preloadedAnswer: res.data.answer, preloadedUserMessage: `O que são os ${cteHook.data.sftpTotal} arquivos aguardando processamento?`, sessionId: res.data.sessionId });
                      } catch (err) {
                        console.error('[Autopilot] sftpChatInit error:', err);
                      } finally {
                        setSftpChatLoading(false);
                      }
                    }}
                    disabled={sftpChatLoading}
                    style={{ background: sftpChatLoading ? AUTOPILOT_COLORS.purpleBgHover : AUTOPILOT_COLORS.purpleBg, border: `1px solid ${AUTOPILOT_COLORS.purpleBorder}`, borderRadius: "4px", color: sftpChatLoading ? AUTOPILOT_COLORS.purpleTextHover : AUTOPILOT_COLORS.purpleText, fontSize: "0.68rem", padding: "2px 7px", cursor: sftpChatLoading ? "not-allowed" : "pointer", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: "4px" }}
                  >
                    <img src={autopilotMiniIcon} alt="" style={{ width: 13, height: 13, objectFit: "contain", opacity: sftpChatLoading ? 0.4 : 1 }} />
                    {sftpChatLoading ? "..." : "Autopilot"}
                  </button>
                  <button
                    disabled={integrandoCte}
                    onClick={function() {
                      setIntegrandoCte(true);
                      const body = {};
                      if (empresaId) body.embarcadorId = empresaId;
                      api.post("/autopilot/insights/cte/integrar", body)
                        .then(function() {
                          setIntegrandoCte(false);
                          setIntegracaoFeedback({ ok: true });
                          setTimeout(function() { setIntegracaoFeedback(null); cteHook.refresh(true); }, 90000);
                        })
                        .catch(function() {
                          setIntegrandoCte(false);
                          setIntegracaoFeedback({ ok: false });
                          setTimeout(function() { setIntegracaoFeedback(null); }, 8000);
                        });
                    }}
                    style={{ background: integrandoCte ? AUTOPILOT_COLORS.infoButtonBgDisabled : AUTOPILOT_COLORS.infoButtonBg, border: `1px solid ${AUTOPILOT_COLORS.infoButtonBorder}`, borderRadius: "4px", color: integrandoCte ? AUTOPILOT_COLORS.infoButtonTextDisabled : AUTOPILOT_COLORS.infoButtonText, fontSize: "0.72rem", padding: "3px 10px", cursor: integrandoCte ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}
                  >
                    {integrandoCte ? "Integrando..." : "Integrar agora"}
                  </button>
                  <button
                    onClick={function() { history.push("/app/flow/sustentacao/importacao"); }}
                    style={{ background: "none", border: `1px solid ${AUTOPILOT_COLORS.infoButtonBorder}`, borderRadius: "4px", color: AUTOPILOT_COLORS.infoButtonTextSoft, fontSize: "0.72rem", padding: "3px 10px", cursor: "pointer", whiteSpace: "nowrap" }}
                    title="Abrir a fila completa de importação (seleção múltipla, integrar/rejeitar em lote)"
                  >
                    Fila de importação
                  </button>
                </div>
                {integracaoFeedback && (
                  <div style={{
                    marginTop: "0.6rem",
                    padding: "0.55rem 0.8rem",
                    borderRadius: 6,
                    fontSize: "0.78rem",
                    lineHeight: 1.45,
                    background: integracaoFeedback.ok ? AUTOPILOT_COLORS.statusSuccessBg : AUTOPILOT_COLORS.statusDangerBg,
                    border: `1px solid ${integracaoFeedback.ok ? AUTOPILOT_COLORS.statusSuccessBorder : AUTOPILOT_COLORS.statusDangerBorder}`,
                    color: integracaoFeedback.ok ? AUTOPILOT_COLORS.statusSuccess : AUTOPILOT_COLORS.statusDanger,
                  }}>
                    {integracaoFeedback.ok
                      ? "CTes enviados para integração. O download foi priorizado — a persistência no banco ocorre via fila e pode levar alguns minutos dependendo do volume atual."
                      : "Não foi possível acionar a integração. Verifique se o serviço está disponível e tente novamente."}
                  </div>
                )}
              </SubCard>
            )}

            {cteHook.data.topInconsistencias && cteHook.data.topInconsistencias.length > 0 && (
              <SubCard title="Tipos de Inconsistência">
                <TopTable
                  items={cteHook.data.topInconsistencias}
                  onItemClick={openCteDetails}
                  onItemReject={handleRejeitarCategoria}
                  onItemExplain={function(item) {
                    const isNfeNaoEncontrada = item.nome === 'NF não encontrada no TMS';
                    const isTagNotaCategoria = item.nome === 'TAG Nota não encontrada';
                    const isCnpjRemetenteCategoria = item.nome === 'CNPJ do Remetente não cadastrado';
                    if (isNfeNaoEncontrada) {
                      const userQuestion = `Há ${item.erros} CTe com NFe não encontrada no TMS. O que está acontecendo e como posso resolver?`;
                      setDiagnosando({ nomeArquivo: '__categoria__' });
                      api.post("/autopilot/diagnostico/nfe-categoria", { cteCount: item.erros })
                        .then(function(res) {
                          setDiagnosando(null);
                          setChatModal({ open: true, categoria: null, count: 0, initialMessage: null, title: item.nome, preloadedAnswer: res.data.answer || null, preloadedUserMessage: userQuestion });
                        })
                        .catch(function() {
                          setDiagnosando(null);
                          setChatModal({ open: true, categoria: item.nome, count: item.erros, initialMessage: null, title: item.nome, preloadedAnswer: null, preloadedUserMessage: null });
                        });
                    } else if (isTagNotaCategoria) {
                      const userQuestion = `Há ${item.erros} CTe com a inconsistência "TAG Nota não encontrada". O que significa isso e como posso resolver?`;
                      setDiagnosando({ nomeArquivo: '__categoria__' });
                      api.post("/autopilot/diagnostico/tag-nota-categoria", { cteCount: item.erros })
                        .then(function(res) {
                          setDiagnosando(null);
                          setChatModal({ open: true, categoria: null, count: 0, initialMessage: null, title: item.nome, preloadedAnswer: res.data.answer || null, preloadedUserMessage: userQuestion });
                        })
                        .catch(function() {
                          setDiagnosando(null);
                          setChatModal({ open: true, categoria: item.nome, count: item.erros, initialMessage: null, title: item.nome, preloadedAnswer: null, preloadedUserMessage: null });
                        });
                    } else if (isCnpjRemetenteCategoria) {
                      const userQuestion = `Há ${item.erros} CTe com "CNPJ do Remetente não cadastrado". O que significa isso e como devo resolver?`;
                      setDiagnosando({ nomeArquivo: '__categoria__' });
                      api.post("/autopilot/diagnostico/cnpj-remetente-categoria")
                        .then(function(res) {
                          setDiagnosando(null);
                          setChatModal({ open: true, categoria: null, count: 0, initialMessage: null, title: item.nome, preloadedAnswer: res.data.answer || null, preloadedUserMessage: userQuestion });
                        })
                        .catch(function() {
                          setDiagnosando(null);
                          setChatModal({ open: true, categoria: item.nome, count: item.erros, initialMessage: null, title: item.nome, preloadedAnswer: null, preloadedUserMessage: null });
                        });
                    } else {
                      const ctx = getInconsistenciaContext(item.nome);
                      const initialMessage = ctx
                        ? `No Monitor de Integrações, há ${item.erros} CTe com a inconsistência "${item.nome}" no backlog.${ctx}\n\nMe explique o que causa esse problema e como posso resolver.`
                        : null;
                      setChatModal({ open: true, categoria: item.nome, count: item.erros, initialMessage, title: null, preloadedAnswer: null, preloadedUserMessage: null });
                    }
                  }}
                  rejeitandoCategoria={rejeitandoCategoria}
                />
              </SubCard>
            )}

            {cteHook.data.answer && (
              <SubCard title="Análise">
                <AnaliseIA text={cteHook.data.answer} />
              </SubCard>
            )}
          </>
        )}

        {/* Faturas */}
        {activeTab === "fatura" && faturaHook.data && (
          <>
            <SubCard title="Backlog Ativo">
              <div style={{ fontSize: "0.82rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: `1px solid ${AUTOPILOT_COLORS.divider}` }}>
                  <span style={{ color: AUTOPILOT_COLORS.textSoft }}>Faturas com inconsistência</span>
                  <span style={{ color: AUTOPILOT_COLORS.statusDanger, fontWeight: 600 }}>
                    {faturaHook.data.pendentes}
                  </span>
                </div>
                {faturaHook.data.maisAntiga && (
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
                    <span style={{ color: AUTOPILOT_COLORS.textSoft }}>Mais antiga no backlog</span>
                    <span style={{ fontWeight: 600, color: semaphoreColor(faturaHook.data.maisAntiga) }}>
                      {timeAgo(faturaHook.data.maisAntiga)}
                    </span>
                  </div>
                )}
                {faturaHook.data.taxaSucesso !== null && (
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
                    <span style={{ color: AUTOPILOT_COLORS.textSoft }}>Taxa de sucesso</span>
                    <span style={{ color: faturaHook.data.taxaSucesso >= 90 ? AUTOPILOT_COLORS.statusSuccess : faturaHook.data.taxaSucesso >= 70 ? AUTOPILOT_COLORS.statusWarning : AUTOPILOT_COLORS.statusDanger, fontWeight: 600 }}>
                      {faturaHook.data.taxaSucesso}%
                    </span>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px", marginTop: "10px" }}>
                <button
                  onClick={function() {
                    const initialMessage = `Há ${faturaHook.data.pendentes} faturas com inconsistência no backlog de integração. Me faça uma análise do que está acontecendo e quais ações devo tomar.`;
                    setChatModal({ open: true, categoria: null, count: faturaHook.data.pendentes, initialMessage, title: "Backlog de Faturas", preloadedAnswer: null, preloadedUserMessage: null });
                  }}
                  style={{ background: AUTOPILOT_COLORS.purpleBg, border: `1px solid ${AUTOPILOT_COLORS.purpleBorder}`, borderRadius: "4px", color: AUTOPILOT_COLORS.purpleText, fontSize: "0.68rem", padding: "2px 7px", cursor: "pointer", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: "4px" }}
                >
                  <img src={autopilotMiniIcon} alt="" style={{ width: 13, height: 13, objectFit: "contain" }} />
                  Autopilot
                </button>
                <button
                  onClick={function() { openFaturaDetails({ nome: "all", label: "Todos" }); }}
                  style={{ background: "none", border: `1px solid ${AUTOPILOT_COLORS.infoButtonBorder}`, color: AUTOPILOT_COLORS.infoButtonTextSoft, borderRadius: "3px", padding: "1px 5px", fontSize: "0.68rem", cursor: "pointer", lineHeight: 1.4 }}
                >
                  Visualizar
                </button>
              </div>
            </SubCard>

            {faturaHook.data.sftpPending !== undefined && (
              <SubCard title="Aguardando Simulação (SFTP)">
                <div style={{ fontSize: "0.82rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: `1px solid ${AUTOPILOT_COLORS.divider}`, marginBottom: "6px" }}>
                    <span style={{ color: AUTOPILOT_COLORS.textSoft }}>Total de arquivos</span>
                    <span style={{ color: AUTOPILOT_COLORS.statusWarning, fontWeight: 600 }}>{faturaHook.data.sftpTotal}</span>
                  </div>
                  {faturaHook.data.sftpPending.map(function(item, i) {
                    return (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0" }}>
                        <span style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem" }}>{item.transportadora}</span>
                        <span style={{ color: AUTOPILOT_COLORS.statusWarning, fontWeight: 600, fontSize: "0.78rem" }}>{item.pendentes}</span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px", marginTop: "10px" }}>
                  <button
                    onClick={function() {
                      const transportadoras = faturaHook.data.sftpPending.map(function(t) { return t.transportadora + ': ' + t.pendentes + ' arquivo(s)'; }).join(', ');
                      const initialMessage = `Há ${faturaHook.data.sftpTotal} fatura(s) aguardando download no SFTP (${transportadoras}). O que pode estar causando o atraso e o que devo fazer?`;
                      setChatModal({ open: true, categoria: null, count: 0, initialMessage, title: "SFTP Faturas", preloadedAnswer: null, preloadedUserMessage: null });
                    }}
                    style={{ background: AUTOPILOT_COLORS.purpleBg, border: `1px solid ${AUTOPILOT_COLORS.purpleBorder}`, borderRadius: "4px", color: AUTOPILOT_COLORS.purpleText, fontSize: "0.68rem", padding: "2px 7px", cursor: "pointer", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: "4px" }}
                  >
                    <img src={autopilotMiniIcon} alt="" style={{ width: 13, height: 13, objectFit: "contain" }} />
                    Autopilot
                  </button>
                </div>
              </SubCard>
            )}

            {faturaHook.data.vencimento && (
              <SubCard title="Vencimento das Faturas">
                <div style={{ fontSize: "0.82rem" }}>
                  {[
                    { label: "Vencidas",         value: faturaHook.data.vencimento.vencida,       color: AUTOPILOT_COLORS.statusDanger },
                    { label: "Vencem em 7 dias",  value: faturaHook.data.vencimento.vence7,        color: AUTOPILOT_COLORS.statusAccent },
                    { label: "Vencem em 15 dias", value: faturaHook.data.vencimento.vence15,       color: AUTOPILOT_COLORS.statusInfo },
                    { label: "Vencem em 30 dias", value: faturaHook.data.vencimento.vence30,       color: AUTOPILOT_COLORS.statusSuccess },
                    { label: "Mais de 30 dias",   value: faturaHook.data.vencimento.futuro,        color: AUTOPILOT_COLORS.statusSuccess },
                    { label: "Sem vencimento",    value: faturaHook.data.vencimento.semVencimento, color: AUTOPILOT_COLORS.textSofter },
                  ].map(function(item, i) {
                    if (item.value === 0) return null;
                    return (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", borderBottom: `1px solid ${AUTOPILOT_COLORS.dividerStrong}` }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", color: AUTOPILOT_COLORS.muted }}>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: item.color, display: "inline-block", flexShrink: 0 }} />
                          {item.label}
                        </span>
                        <span style={{ color: item.color, fontWeight: 600 }}>{item.value}</span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px", marginTop: "10px" }}>
                  <button
                    onClick={function() {
                      const v = faturaHook.data.vencimento;
                      const initialMessage = `Análise de vencimento das faturas no backlog:\n- Vencidas: ${v.vencida}\n- Vencem em 7 dias: ${v.vence7}\n- Vencem em 15 dias: ${v.vence15}\n- Vencem em 30 dias: ${v.vence30}\n- Mais de 30 dias: ${v.futuro}\n- Sem vencimento: ${v.semVencimento}\n\nQual é a situação mais crítica e o que devo fazer?`;
                      setChatModal({ open: true, categoria: null, count: 0, initialMessage, title: "Vencimento de Faturas", preloadedAnswer: null, preloadedUserMessage: null });
                    }}
                    style={{ background: AUTOPILOT_COLORS.purpleBg, border: `1px solid ${AUTOPILOT_COLORS.purpleBorder}`, borderRadius: "4px", color: AUTOPILOT_COLORS.purpleText, fontSize: "0.68rem", padding: "2px 7px", cursor: "pointer", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: "4px" }}
                  >
                    <img src={autopilotMiniIcon} alt="" style={{ width: 13, height: 13, objectFit: "contain" }} />
                    Autopilot
                  </button>
                  <button
                    onClick={function() { openFaturaDetails({ nome: "all", label: "Todos" }); }}
                    style={{ background: "none", border: `1px solid ${AUTOPILOT_COLORS.infoButtonBorder}`, color: AUTOPILOT_COLORS.infoButtonTextSoft, borderRadius: "3px", padding: "1px 5px", fontSize: "0.68rem", cursor: "pointer", lineHeight: 1.4 }}
                  >
                    Visualizar
                  </button>
                  <button
                    onClick={function() { history.push("/app/flow/sustentacao/importacao?tab=fatura"); }}
                    style={{ background: "none", border: `1px solid ${AUTOPILOT_COLORS.infoButtonBorder}`, color: AUTOPILOT_COLORS.infoButtonTextSoft, borderRadius: "3px", padding: "1px 5px", fontSize: "0.68rem", cursor: "pointer", lineHeight: 1.4 }}
                    title="Abrir a fila completa de importação (seleção múltipla, rejeitar em lote)"
                  >
                    Fila de importação
                  </button>
                </div>
              </SubCard>
            )}

            {faturaHook.data.topInconsistencias && faturaHook.data.topInconsistencias.length > 0 && (
              <SubCard title="Tipos de Inconsistência">
                <TopTable
                  items={faturaHook.data.topInconsistencias}
                  onItemClick={openFaturaDetails}
                  onItemReject={handleRejeitarFaturaCategoria}
                  diagnosando={diagnosando}
                  onItemExplain={function(item) {
                    if (item.nome === 'CTe não encontrado no sistema') {
                      setDiagnosando({ nomeArquivo: item.nome });
                      api.get("/autopilot/insights/fatura/details", { params: { categoria: item.nome } })
                        .then(function(res) {
                          const rows = res.data || [];

                          // Separa em dois grupos: CTe confirmado no backlog vs. não localizado
                          const comBacklog = rows.filter(function(r) { return r.cteBacklog && r.cteBacklog.length > 0; });
                          const semBacklog = rows.filter(function(r) { return !r.cteBacklog || r.cteBacklog.length === 0; });

                          // Extrai CTes únicos por transportadora para os não encontrados (semBacklog)
                          const cteNumRegex = /CTe.*?Nr\.\s*(\d+)/gi;
                          const ctePorTransportadora = {};
                          semBacklog.forEach(function(row) {
                            const nums = Array.from((row.Inconsistencias || '').matchAll(cteNumRegex)).map(function(m) { return m[1].replace(/^0+/, '') || m[1]; });
                            if (nums.length) {
                              const key = row.Fantasia || 'Desconhecida';
                              if (!ctePorTransportadora[key]) ctePorTransportadora[key] = new Set();
                              nums.forEach(function(n) { ctePorTransportadora[key].add(n); });
                            }
                          });

                          // Consolida CTes únicos no backlog
                          const cteBacklogMap = {};
                          comBacklog.forEach(function(r) {
                            (r.cteBacklog || []).forEach(function(cte) {
                              if (!cteBacklogMap[cte.Numero]) cteBacklogMap[cte.Numero] = { inconsistencia: cte.Inconsistencias, faturas: 0 };
                              cteBacklogMap[cte.Numero].faturas++;
                            });
                          });

                          const todosNumerosNaoEncontrados = Object.values(ctePorTransportadora).flatMap(function(s) { return Array.from(s); });

                          const secaoBacklog = Object.keys(cteBacklogMap).length > 0
                            ? `**${comBacklog.length} fatura(s) com CTe já no backlog com inconsistência própria:**\n` +
                              Object.keys(cteBacklogMap).map(function(num) {
                                const info = cteBacklogMap[num];
                                return `• CTe Nr. ${num} (${info.faturas} fatura(s)): ${info.inconsistencia || 'inconsistência não identificada'}`;
                              }).join('\n')
                            : '';

                          const secaoNaoEncontrado = Object.keys(ctePorTransportadora).length > 0
                            ? `**${semBacklog.length} fatura(s) com CTe não localizado no sistema — requer investigação:**\n` +
                              Object.entries(ctePorTransportadora).map(function(entry) {
                                return `• Transportadora ${entry[0]}: CTes ${Array.from(entry[1]).join(', ')}`;
                              }).join('\n')
                            : '';

                          const acoesIA = [];
                          if (todosNumerosNaoEncontrados.length > 0) {
                            acoesIA.push(`1. Use a ferramenta verificar_cte_sftp para checar se os CTes [${todosNumerosNaoEncontrados.join(', ')}] estão nos diretórios SFTP de entrada aguardando processamento.`);
                            acoesIA.push(`2. Para os CTes que NÃO estiverem no SFTP, busque os contatos de email da transportadora correspondente e, com confirmação do usuário, envie um email solicitando o envio imediato dos CTes em falta listando os números.`);
                          }
                          if (comBacklog.length > 0) {
                            acoesIA.push(`${acoesIA.length + 1}. Para os CTes que estão no backlog com inconsistência própria, explique o que causou cada inconsistência e quais são as ações necessárias para resolução.`);
                          }

                          const sectionSep = secaoBacklog && secaoNaoEncontrado ? '\n\n' : '';
                          const initialMessage =
                            `Diagnóstico das faturas com inconsistência "CTe não encontrado no sistema" (${rows.length} total):\n\n` +
                            secaoBacklog + sectionSep + secaoNaoEncontrado +
                            (acoesIA.length > 0 ? `\n\nPor favor, execute as seguintes ações:\n` + acoesIA.join('\n') : '');

                          const displayMessage = `Diagnóstico: ${rows.length} fatura(s) com CTe não encontrado no sistema.`;
                          setDiagnosando(null);
                          setChatModal({ open: true, categoria: item.nome, count: item.erros, initialMessage, displayMessage, title: item.nome, preloadedAnswer: null, preloadedUserMessage: null });
                        })
                        .catch(function() {
                          setDiagnosando(null);
                          const initialMessage = `No Monitor de Integrações, há ${item.erros} fatura(s) com a inconsistência "${item.nome}" no backlog. Me explique o que causa esse problema e como posso resolver.`;
                          setChatModal({ open: true, categoria: item.nome, count: item.erros, initialMessage, title: item.nome, preloadedAnswer: null, preloadedUserMessage: null });
                        });
                      return;
                    }
                    const initialMessage = `No Monitor de Integrações, há ${item.erros} fatura(s) com a inconsistência "${item.nome}" no backlog. Me explique o que causa esse problema e como posso resolver.`;
                    setChatModal({ open: true, categoria: item.nome, count: item.erros, initialMessage, title: item.nome, preloadedAnswer: null, preloadedUserMessage: null });
                  }}
                  rejeitandoCategoria={rejeitandoCategoria}
                />
              </SubCard>
            )}

            {faturaHook.data.answer && (
              <SubCard title="Análise">
                <AnaliseIA text={faturaHook.data.answer} />
              </SubCard>
            )}
          </>
        )}

        {/* Nota Fiscal */}
        {activeTab === "nf" && nfHook.data && (
          <>
            <SubCard title="Erros por Classificação">
              <div style={{ fontSize: "0.82rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
                  <span style={{ color: AUTOPILOT_COLORS.textSoft }}>Negócio</span>
                  <span style={{ color: AUTOPILOT_COLORS.statusDanger, fontWeight: 600 }}>
                    {(nfHook.data.errorTypeCount && nfHook.data.errorTypeCount["business-error"]) || 0}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
                  <span style={{ color: AUTOPILOT_COLORS.textSoft }}>Técnico</span>
                  <span style={{ color: AUTOPILOT_COLORS.statusWarning, fontWeight: 600 }}>
                    {(nfHook.data.errorTypeCount && nfHook.data.errorTypeCount["technical-error"]) || 0}
                  </span>
                </div>
                {nfHook.data.totalErrors > 0 && (
                  <div style={{ marginTop: "0.6rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.6rem", flexWrap: "wrap" }}>
                    <span style={{ color: AUTOPILOT_COLORS.textSofter, fontSize: "0.74rem" }}>
                      Tenta reaplicar todas as NFe das últimas 24h com o payload original.
                    </span>
                    <button
                      type="button"
                      disabled={reprocessandoTodasNF}
                      onClick={function() {
                        if (reprocessandoTodasNF) return;
                        if (!window.confirm("Reprocessar TODAS as " + nfHook.data.totalErrors + " NFe que falharam nas últimas 24h?")) return;
                        reprocessarTodasNF(null);
                      }}
                      style={{ background: reprocessandoTodasNF ? AUTOPILOT_COLORS.infoButtonBgDisabled : AUTOPILOT_COLORS.infoButtonBg, border: `1px solid ${AUTOPILOT_COLORS.infoButtonBorder}`, borderRadius: "4px", color: reprocessandoTodasNF ? AUTOPILOT_COLORS.infoButtonTextDisabled : AUTOPILOT_COLORS.infoButtonText, fontSize: "0.72rem", padding: "3px 10px", cursor: reprocessandoTodasNF ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}
                    >
                      {reprocessandoTodasNF ? "Reprocessando..." : "Reprocessar todos"}
                    </button>
                  </div>
                )}
                {nfReprocessFeedback && (
                  <div style={{
                    marginTop: "0.6rem",
                    padding: "0.55rem 0.8rem",
                    borderRadius: 6,
                    fontSize: "0.78rem",
                    lineHeight: 1.45,
                    background: nfReprocessFeedback.ok ? AUTOPILOT_COLORS.statusSuccessBg : AUTOPILOT_COLORS.statusDangerBg,
                    border: `1px solid ${nfReprocessFeedback.ok ? AUTOPILOT_COLORS.statusSuccessBorder : AUTOPILOT_COLORS.statusDangerBorder}`,
                    color: nfReprocessFeedback.ok ? AUTOPILOT_COLORS.statusSuccess : AUTOPILOT_COLORS.statusDanger,
                  }}>
                    {nfReprocessFeedback.error
                      ? "Falha: " + nfReprocessFeedback.error
                      : nfReprocessFeedback.ok
                        ? `Reprocessadas ${nfReprocessFeedback.okCount} de ${nfReprocessFeedback.total} NFe com sucesso.`
                        : `Reprocessadas ${nfReprocessFeedback.okCount} de ${nfReprocessFeedback.total} — ${nfReprocessFeedback.failCount} falharam.`}
                  </div>
                )}
              </div>
            </SubCard>

            {nfHook.data.topErrMessages && nfHook.data.topErrMessages.length > 0 && (
              <SubCard title="Principais Erros">
                <TopTable
                  items={nfHook.data.topErrMessages}
                  onItemClick={openNFDetails}
                  onItemExplain={diagnoseNFCategoria}
                  diagnosando={diagnosando && diagnosando.nomeArquivo && diagnosando.nomeArquivo.indexOf('__nf-categoria__:') === 0 ? { nomeArquivo: diagnosando.nomeArquivo.replace('__nf-categoria__:', '') } : null}
                />
              </SubCard>
            )}

            {nfHook.data.answer && (
              <SubCard title="Análise">
                <AnaliseIA text={nfHook.data.answer} />
              </SubCard>
            )}
          </>
        )}
      </CardBody>

      {/* Modal de detalhes de erros de frete */}
      {ReactDOM.createPortal(
        <Modal isOpen={freteDetailsModal.open} toggle={function() { setFreteDetailsModal(function(m) { return Object.assign({}, m, { open: false }); }); }} size="lg" fade={false} modalClassName="modal-align-top">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px 0" }}>
            <h5 style={{ color: AUTOPILOT_COLORS.title, margin: 0, fontSize: "0.95rem", fontWeight: 700 }}>{freteDetailsModal.title}</h5>
            <button onClick={function() { setFreteDetailsModal(function(m) { return Object.assign({}, m, { open: false }); }); }} style={{ background: "none", border: "none", color: AUTOPILOT_COLORS.statusNeutral, fontSize: 20, cursor: "pointer" }}>×</button>
          </div>
          <ModalBody style={{ maxHeight: "60vh", overflowY: "auto", paddingTop: 12 }}>
            {freteDetailsModal.loading ? (
              <div className="text-center py-4"><ClipLoader size={24} color={AUTOPILOT_COLORS.statusInfo} /></div>
            ) : freteDetailsModal.data.length === 0 ? (
              <p style={{ color: AUTOPILOT_COLORS.statusNeutral, fontSize: "0.85rem", textAlign: "center", padding: "24px 0" }}>Nenhum erro encontrado.</p>
            ) : (function() {
              // Group duplicate errors by key (tipo + mensagem)
              var grouped = [];
              var seen = {};
              freteDetailsModal.data.forEach(function(item) {
                var key = item.tipo + "||" + item.mensagem;
                var num = item.numeroCte != null ? String(item.numeroCte) : null;
                if (seen[key] !== undefined) {
                  var g = grouped[seen[key]];
                  g.count++;
                  if (num && g.numeros.indexOf(num) === -1) g.numeros.push(num);
                  if (item.complementar) g.complementar = true;
                } else {
                  seen[key] = grouped.length;
                  grouped.push(Object.assign({}, item, { count: 1, numeros: num ? [num] : [], complementar: Boolean(item.complementar) }));
                }
              });

              // Parse "Contexto: K: V | K: V | ..." into display fields, hiding IDs
              var HIDDEN_FIELDS = { Transportadora: true, Origem: true, Destino: true };
              var ID_PATTERN = /Id$|^id[A-Z]/;
              // dataEmissaoNF chega em ISO (...Z) ou JS Date string. Formata como
              // dd/mm/yyyy. No caso ISO, lê a data literal (sem converter fuso),
              // senão a meia-noite UTC vira o dia anterior em BRT.
              function formatDataNF(v) {
                var iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
                if (iso) return iso[3] + "/" + iso[2] + "/" + iso[1];
                var dt = new Date(v);
                if (!isNaN(dt.getTime())) {
                  var dd = ("0" + dt.getDate()).slice(-2);
                  var mm = ("0" + (dt.getMonth() + 1)).slice(-2);
                  return dd + "/" + mm + "/" + dt.getFullYear();
                }
                return v;
              }
              function parseContexto(msg) {
                var ctxIdx = msg.indexOf("Contexto:");
                if (ctxIdx === -1) return null;
                var parts = msg.slice(ctxIdx + 9).split("|");
                var fields = [];
                parts.forEach(function(part) {
                  var colon = part.indexOf(":");
                  if (colon === -1) return;
                  var k = part.slice(0, colon).trim();
                  var v = part.slice(colon + 1).trim();
                  if (HIDDEN_FIELDS[k] || ID_PATTERN.test(k)) return;
                  if (k === "dataEmissaoNF" && v) {
                    v = formatDataNF(v);
                    k = "Data NF";
                  }
                  fields.push({ k: k, v: v });
                });
                return fields;
              }

              return (
                <div>
                  <p style={{ color: AUTOPILOT_COLORS.statusNeutral, fontSize: "0.78rem", marginBottom: 10 }}>
                    {freteDetailsModal.data.length} CTe(s) · {grouped.length} padrão(ões) distinto(s)
                  </p>
                  {grouped.map(function(item, i) {
                    var ctx = parseContexto(item.mensagem || "");
                    var ctxIdx = (item.mensagem || "").indexOf("Contexto:");
                    var headline = ctxIdx !== -1 ? item.mensagem.slice(0, ctxIdx).trim() : item.mensagem;
                    return (
                      <div key={i} style={{ borderBottom: `1px solid ${AUTOPILOT_COLORS.borderSoft}`, padding: "12px 0" }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
                          <span style={{ background: item.tipo === "CTe" ? `color-mix(in srgb, ${AUTOPILOT_COLORS.statusInfo} 15%, transparent)` : `color-mix(in srgb, ${AUTOPILOT_COLORS.statusSuccess} 15%, transparent)`, border: "1px solid " + (item.tipo === "CTe" ? `color-mix(in srgb, ${AUTOPILOT_COLORS.statusInfo} 40%, transparent)` : `color-mix(in srgb, ${AUTOPILOT_COLORS.statusSuccess} 40%, transparent)`), borderRadius: 3, color: item.tipo === "CTe" ? AUTOPILOT_COLORS.statusInfo : AUTOPILOT_COLORS.statusSuccess, padding: "1px 6px", fontSize: "0.7rem", fontWeight: 600 }}>{item.tipo}</span>
                          {item.complementar && <span title="CTe de Complemento (TipoConhecimento=1) — cadastro de tabela costuma ser separado" style={{ background: `color-mix(in srgb, ${AUTOPILOT_COLORS.statusWarning} 15%, transparent)`, border: `1px solid color-mix(in srgb, ${AUTOPILOT_COLORS.statusWarning} 40%, transparent)`, borderRadius: 3, color: AUTOPILOT_COLORS.statusWarning, padding: "1px 6px", fontSize: "0.7rem", fontWeight: 600 }}>Complementar</span>}
                          {item.transportadora !== "—" && <span style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, fontSize: "0.82rem" }}>{item.transportadora}</span>}
                          {item.rota !== "—" && <span style={{ color: AUTOPILOT_COLORS.statusNeutral, fontSize: "0.8rem" }}>· {item.rota}</span>}
                          {item.count > 1 && <span style={{ marginLeft: "auto", background: `color-mix(in srgb, ${AUTOPILOT_COLORS.statusDanger} 15%, transparent)`, border: `1px solid color-mix(in srgb, ${AUTOPILOT_COLORS.statusDanger} 40%, transparent)`, borderRadius: 10, color: AUTOPILOT_COLORS.statusDanger, padding: "1px 8px", fontSize: "0.7rem", fontWeight: 600 }}>{item.count} CTe(s)</span>}
                        </div>
                        <p style={{ color: AUTOPILOT_COLORS.statusDanger, margin: "0 0 8px", fontSize: "0.8rem" }}>{headline}</p>
                        {ctx && ctx.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 10px" }}>
                            {ctx.map(function(f) {
                              return (
                                <span key={f.k} style={{ fontSize: "0.72rem", color: AUTOPILOT_COLORS.statusNeutral }}>
                                  <span style={{ color: AUTOPILOT_COLORS.textSofter }}>{f.k}:</span> <span style={{ color: AUTOPILOT_COLORS.textSoft }}>{f.v}</span>
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {item.numeros && item.numeros.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                            {item.numeros.map(function(n) {
                              return (
                                <span key={n} style={{ fontSize: "0.7rem", fontWeight: 600, background: `color-mix(in srgb, ${AUTOPILOT_COLORS.statusInfo} 12%, transparent)`, border: `1px solid color-mix(in srgb, ${AUTOPILOT_COLORS.statusInfo} 35%, transparent)`, borderRadius: 3, color: AUTOPILOT_COLORS.statusInfo, padding: "1px 6px" }}>
                                  CTe {n}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </ModalBody>
        </Modal>,
        document.body
      )}

      <ChatModal
        isOpen={chatModal.open}
        onClose={() => setChatModal({ open: false, categoria: null, count: 0, initialMessage: null, title: null, preloadedAnswer: null, preloadedUserMessage: null, sessionId: null })}
        categoria={chatModal.categoria}
        count={chatModal.count}
        initialMessage={chatModal.initialMessage}
        displayMessage={chatModal.displayMessage}
        title={chatModal.title}
        preloadedAnswer={chatModal.preloadedAnswer}
        preloadedUserMessage={chatModal.preloadedUserMessage}
        sessionId={chatModal.sessionId}
        empresaId={empresaId}
      />

      <XmlViewerModal
        isOpen={xmlModal.open}
        onClose={function() { setXmlModal({ open: false, nomeArquivo: null, idTransportadora: null }); }}
        nomeArquivo={xmlModal.nomeArquivo}
        idTransportadora={xmlModal.idTransportadora}
        embarcadorId={empresaId}
      />

      <PayloadViewerModal
        isOpen={payloadModal.open}
        onClose={function() { setPayloadModal({ open: false, chaveacesso: null, numero: null }); }}
        chaveacesso={payloadModal.chaveacesso}
        numero={payloadModal.numero}
        embarcadorId={empresaId}
      />

      {ReactDOM.createPortal(
        <Modal
          isOpen={nfModal.open}
          toggle={function() { setNfModal(function(m) { return Object.assign({}, m, { open: false }); }); }}
          size="xl"
          fade={false}
          modalClassName="modal-align-top"
          contentClassName="cte-details-modal"
          backdropClassName="cte-modal-backdrop"
          wrapClassName="cte-modal-wrap"
          style={{ maxWidth: "1400px", width: "96vw" }}
        >
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "var(--everlog-card-hero-bg)",
            borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
            padding: "14px 20px",
          }}>
            <span style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, fontSize: 15 }}>
              NFe com erro: {nfModal.title}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {nfModal.data && nfModal.data.length > 0 && (
                <button
                  type="button"
                  disabled={reprocessandoTodasNF}
                  onClick={function() {
                    if (reprocessandoTodasNF) return;
                    if (!window.confirm("Reprocessar " + nfModal.data.length + " NFe com este erro?")) return;
                    reprocessarTodasNF(nfModal.errorMessage);
                  }}
                  style={{ background: reprocessandoTodasNF ? AUTOPILOT_COLORS.infoButtonBgDisabled : AUTOPILOT_COLORS.infoButtonBg, border: `1px solid ${AUTOPILOT_COLORS.infoButtonBorder}`, borderRadius: "4px", color: reprocessandoTodasNF ? AUTOPILOT_COLORS.infoButtonTextDisabled : AUTOPILOT_COLORS.infoButtonText, fontSize: "0.72rem", padding: "3px 10px", cursor: reprocessandoTodasNF ? "not-allowed" : "pointer" }}
                >
                  {reprocessandoTodasNF ? "Reprocessando..." : "Reprocessar todos"}
                </button>
              )}
              <button
                onClick={function() { setNfModal(function(m) { return Object.assign({}, m, { open: false }); }); }}
                style={{ background: "none", border: "none", color: AUTOPILOT_COLORS.title, fontSize: 24, lineHeight: 1, cursor: "pointer", padding: "0 4px" }}
              >×</button>
            </div>
          </div>
          <ModalBody style={{ maxHeight: "70vh", overflow: "auto", padding: 0 }}>
            {nfModal.loading ? (
              <div className="text-center py-4"><ClipLoader size={24} color={AUTOPILOT_COLORS.statusInfo} /></div>
            ) : nfModal.data.length === 0 ? (
              <p style={{ color: AUTOPILOT_COLORS.statusNeutral, fontSize: "0.85rem", textAlign: "center", padding: "24px 0" }}>Nenhuma NFe com este erro nas últimas 24h.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                <thead>
                  <tr style={{ background: "var(--everlog-card-surface-elevated)", borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`, color: AUTOPILOT_COLORS.textSofter, fontSize: "0.74rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    <th style={{ padding: "10px 16px", textAlign: "left" }}>Nº NF</th>
                    <th style={{ padding: "10px 16px", textAlign: "left" }}>Sér.</th>
                    <th style={{ padding: "10px 16px", textAlign: "left" }}>Chave</th>
                    <th style={{ padding: "10px 16px", textAlign: "left" }}>Destinatário/Remetente</th>
                    <th style={{ padding: "10px 16px", textAlign: "left" }}>Recebido em</th>
                    <th style={{ padding: "10px 16px", textAlign: "left" }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {nfModal.data.map(function(row, i) {
                    const isReprocessando = reprocessandoNF === row.chaveacesso;
                    return (
                      <tr key={row.chaveacesso} style={{
                        background: i % 2 === 0 ? "var(--everlog-card-surface)" : "var(--everlog-card-surface-flat)",
                        borderTop: `1px solid ${AUTOPILOT_COLORS.border}`,
                        color: AUTOPILOT_COLORS.text,
                      }}>
                        <td style={{ padding: "9px 16px", whiteSpace: "nowrap" }}>{row.numero || "—"}</td>
                        <td style={{ padding: "9px 16px", whiteSpace: "nowrap" }}>{row.serie || "—"}</td>
                        <td style={{ padding: "9px 16px", fontFamily: "monospace", fontSize: "0.7rem", color: AUTOPILOT_COLORS.textSofter }} title={row.chaveacesso}>
                          {row.chaveacesso ? row.chaveacesso.slice(0, 8) + "…" + row.chaveacesso.slice(-8) : "—"}
                        </td>
                        <td style={{ padding: "9px 16px", maxWidth: "260px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.razaoSocial}>
                          {row.razaoSocial || "—"}
                        </td>
                        <td style={{ padding: "9px 16px", whiteSpace: "nowrap", color: AUTOPILOT_COLORS.textSofter, fontSize: "0.76rem" }}>
                          {row.ts ? new Date(row.ts).toLocaleString("pt-BR") : "—"}
                        </td>
                        <td style={{ padding: "8px 16px", whiteSpace: "nowrap" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                            <button
                              onClick={function() { setPayloadModal({ open: true, chaveacesso: row.chaveacesso, numero: row.numero }); }}
                              style={{ background: "none", border: `1px solid color-mix(in srgb, ${AUTOPILOT_COLORS.statusWarning} 40%, transparent)`, borderRadius: "3px", color: `color-mix(in srgb, ${AUTOPILOT_COLORS.statusWarning} 80%, transparent)`, fontSize: "0.68rem", padding: "1px 5px", cursor: "pointer", lineHeight: 1.4 }}
                            >Payload</button>
                            <button
                              onClick={function() { if (!isReprocessando) reprocessarNF(row.chaveacesso); }}
                              disabled={isReprocessando}
                              style={{ background: isReprocessando ? "transparent" : AUTOPILOT_COLORS.infoButtonBg, border: `1px solid ${AUTOPILOT_COLORS.infoButtonBorder}`, borderRadius: "3px", color: isReprocessando ? AUTOPILOT_COLORS.textSofter : AUTOPILOT_COLORS.infoButtonText, fontSize: "0.68rem", padding: "1px 5px", cursor: isReprocessando ? "default" : "pointer", lineHeight: 1.4 }}
                            >{isReprocessando ? "..." : "Reprocessar"}</button>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </ModalBody>
        </Modal>,
        document.body,
      )}

      <TxtViewerModal
        isOpen={txtModal.open}
        onClose={function() { setTxtModal({ open: false, nomeArquivo: null, idTransportadora: null }); }}
        nomeArquivo={txtModal.nomeArquivo}
        idTransportadora={txtModal.idTransportadora}
        embarcadorId={empresaId}
      />

      <Modal isOpen={modal.open} toggle={closeModal} size="xl" contentClassName="cte-details-modal" backdropClassName="cte-modal-backdrop" wrapClassName="cte-modal-wrap" fade={false} style={{ maxWidth: "1400px", width: "96vw" }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "var(--everlog-card-hero-bg)",
          borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
          padding: "16px 20px",
        }}>
          <span style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, fontSize: 15 }}>
            {modal.source === "fatura"
              ? (modal.title === "Todos" ? "Backlog Ativo — Todas as Faturas com inconsistência" : "Faturas com inconsistência: " + modal.title)
              : (modal.title === "Todos" ? "Backlog Ativo — Todos os CTe com inconsistência" : "CTe com inconsistência: " + modal.title)}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", borderRight: `1px solid ${AUTOPILOT_COLORS.border}`, paddingRight: "10px" }}>
              <span style={{ fontSize: 12, color: AUTOPILOT_COLORS.textSofter, whiteSpace: "nowrap" }}>Rejeitar até</span>
              <input
                type="date"
                value={rejeitarPorData.dataLimite}
                onChange={function(e) { var v = e.target.value; setRejeitarPorData(function(s) { return Object.assign({}, s, { dataLimite: v, confirmando: false }); }); }}
                style={{ background: "var(--everlog-card-surface-flat)", border: `1px solid ${AUTOPILOT_COLORS.border}`, borderRadius: "4px", color: AUTOPILOT_COLORS.title, fontSize: 12, padding: "3px 6px", outline: "none" }}
              />
              {rejeitarPorData.confirmando ? (
                <>
                  <span style={{ fontSize: 11, color: AUTOPILOT_COLORS.statusDanger, whiteSpace: "nowrap" }}>Confirmar?</span>
                  <button
                    onClick={handleRejeitarPorData}
                    style={{ background: AUTOPILOT_COLORS.statusDanger, border: "none", borderRadius: "3px", color: AUTOPILOT_COLORS.onAccent, fontSize: 11, padding: "2px 8px", cursor: "pointer" }}
                  >Sim</button>
                  <button
                    onClick={function() { setRejeitarPorData(function(s) { return Object.assign({}, s, { confirmando: false }); }); }}
                    style={{ background: AUTOPILOT_COLORS.borderFaint, border: "none", borderRadius: "3px", color: AUTOPILOT_COLORS.textSoft, fontSize: 11, padding: "2px 8px", cursor: "pointer" }}
                  >Não</button>
                </>
              ) : (
                <button
                  onClick={handleRejeitarPorData}
                  disabled={!rejeitarPorData.dataLimite || rejeitarPorData.loading}
                  style={{ background: rejeitarPorData.dataLimite ? `color-mix(in srgb, ${AUTOPILOT_COLORS.statusDanger} 15%, transparent)` : AUTOPILOT_COLORS.divider, border: `1px solid color-mix(in srgb, ${AUTOPILOT_COLORS.statusDanger} 40%, transparent)`, borderRadius: "3px", color: rejeitarPorData.dataLimite ? AUTOPILOT_COLORS.statusDanger : AUTOPILOT_COLORS.textFaint, fontSize: 11, padding: "2px 8px", cursor: rejeitarPorData.dataLimite ? "pointer" : "default", whiteSpace: "nowrap" }}
                >
                  {rejeitarPorData.loading ? "Rejeitando..." : "Rejeitar"}
                </button>
              )}
            </div>
            {modal.source === "fatura" && transportadorasDoModal.length > 0 && (
              <select
                value={modalFiltroTransportadora}
                onChange={function(e) { setModalFiltroTransportadora(e.target.value); }}
                style={{
                  background: "var(--everlog-card-surface-flat)",
                  border: `1px solid ${AUTOPILOT_COLORS.border}`,
                  borderRadius: "4px",
                  color: AUTOPILOT_COLORS.title,
                  fontSize: 13,
                  padding: "4px 8px",
                  outline: "none",
                  maxWidth: 180,
                }}
              >
                <option value="">Todas as transportadoras</option>
                {transportadorasDoModal.map(function(t) {
                  return <option key={t} value={t}>{t}</option>;
                })}
              </select>
            )}
            {modal.source === "fatura" && (
              <select
                value={modalFiltroInconsistencia}
                onChange={function(e) { setModalFiltroInconsistencia(e.target.value); }}
                style={{
                  background: "var(--everlog-card-surface-flat)",
                  border: `1px solid ${AUTOPILOT_COLORS.border}`,
                  borderRadius: "4px",
                  color: AUTOPILOT_COLORS.title,
                  fontSize: 13,
                  padding: "4px 8px",
                  outline: "none",
                  maxWidth: 220,
                }}
              >
                <option value="">Todas as inconsistências</option>
                {FATURA_CATEGORIAS_FRONT.map(function(c) {
                  return <option key={c} value={c}>{c}</option>;
                })}
              </select>
            )}
            <input
              type="text"
              placeholder="Buscar arquivo..."
              value={modalSearch}
              onChange={function(e) { setModalSearch(e.target.value); }}
              style={{
                background: "var(--everlog-card-surface-flat)",
                border: `1px solid ${AUTOPILOT_COLORS.border}`,
                borderRadius: "4px",
                color: AUTOPILOT_COLORS.title,
                fontSize: 13,
                padding: "4px 10px",
                outline: "none",
                width: 200,
              }}
            />
            <button onClick={closeModal} style={{
              background: "none", border: "none", color: AUTOPILOT_COLORS.title,
              fontSize: 24, lineHeight: 1, cursor: "pointer", padding: "0 4px",
            }}>×</button>
          </div>
        </div>
        <ModalBody style={{ background: "var(--everlog-card-surface)", color: AUTOPILOT_COLORS.title, maxHeight: "62vh", overflow: "auto", padding: "0" }}>
          {modal.loading ? (
            <div style={{ textAlign: "center", padding: "32px" }}>
              <ClipLoader size={32} color={AUTOPILOT_COLORS.statusInfo} />
            </div>
          ) : modal.data.length > 0 ? (
            <div>
              {(modalSearch || modalFiltroTransportadora || modalFiltroInconsistencia) && (
                <div style={{ padding: "6px 16px", background: "var(--everlog-card-surface-section)", fontSize: 12, color: AUTOPILOT_COLORS.muted, borderBottom: `1px solid ${AUTOPILOT_COLORS.border}` }}>
                  {modal.data.filter(passesModalFilters).length} resultado(s) de {modal.data.length}
                  {modalSearch && ` · busca "${modalSearch}"`}
                  {modalFiltroTransportadora && ` · ${modalFiltroTransportadora}`}
                  {modalFiltroInconsistencia && ` · ${modalFiltroInconsistencia}`}
                </div>
              )}
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "var(--everlog-cte-table-head-bg)", color: "var(--everlog-cte-table-head-text)", textAlign: "left" }}>
                    <th style={{ padding: "10px 16px", fontWeight: 600, whiteSpace: "nowrap" }}>Número</th>
                    <th style={{ padding: "10px 16px", fontWeight: 600, whiteSpace: "nowrap" }}>Transportadora</th>
                    <th style={{ padding: "10px 16px", fontWeight: 600, whiteSpace: "nowrap" }}>Nome Arquivo</th>
                    <th style={{ padding: "10px 16px", fontWeight: 600, whiteSpace: "nowrap" }}>Data Emissão</th>
                    {modal.source === "fatura" && <th style={{ padding: "10px 16px", fontWeight: 600, whiteSpace: "nowrap" }}>Valor Total</th>}
                    {modal.source !== "fatura" && <th style={{ padding: "10px 16px", fontWeight: 600, whiteSpace: "nowrap" }}>Inconsistência</th>}
                    <th style={{ padding: "10px 16px", fontWeight: 600, whiteSpace: "nowrap" }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {modal.data.filter(passesModalFilters).map(function(row, i) {
                    return (
                      <React.Fragment key={i}>
                      <tr style={{
                        background: i % 2 === 0 ? "var(--everlog-card-surface)" : "var(--everlog-card-surface-flat)",
                        borderTop: `1px solid ${AUTOPILOT_COLORS.border}`,
                        color: AUTOPILOT_COLORS.text,
                      }}>
                        <td style={{ padding: "9px 16px", whiteSpace: "nowrap" }}>{row.Numero}</td>
                        <td style={{ padding: "9px 16px", whiteSpace: "nowrap" }}>{row.Fantasia}</td>
                        <td style={{ padding: "9px 16px", maxWidth: "260px", whiteSpace: "nowrap" }} title={row.NomeArquivo}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", display: "inline-block", maxWidth: row.cteBacklog && row.cteBacklog.length > 0 ? "150px" : "100%", verticalAlign: "middle" }}>{row.NomeArquivo}</span>
                          {row.cteBacklog && row.cteBacklog.length > 0 && (
                            <button
                              onClick={function(e) {
                                e.stopPropagation();
                                setExpandedCteRows(function(prev) {
                                  const next = new Set(prev);
                                  next.has(row.Id) ? next.delete(row.Id) : next.add(row.Id);
                                  return next;
                                });
                              }}
                              title="Ver inconsistências dos CTes no backlog"
                              style={{ marginLeft: 6, background: `color-mix(in srgb, ${AUTOPILOT_COLORS.statusWarning} 15%, transparent)`, border: `1px solid color-mix(in srgb, ${AUTOPILOT_COLORS.statusWarning} 50%, transparent)`, borderRadius: 3, color: AUTOPILOT_COLORS.statusWarning, fontSize: "0.65rem", padding: "1px 5px", cursor: "pointer", whiteSpace: "nowrap", verticalAlign: "middle" }}
                            >CTe no backlog ({row.cteBacklog.length}) {expandedCteRows.has(row.Id) ? "▲" : "▼"}</button>
                          )}
                        </td>
                        <td style={{ padding: "9px 16px", whiteSpace: "nowrap" }}>{row.Dat_Emissao ? new Date(row.Dat_Emissao).toLocaleString("pt-BR") : ""}</td>
                        {modal.source === "fatura" && <td style={{ padding: "9px 16px", whiteSpace: "nowrap" }}>{row.Val_TotalFatura ? Number(row.Val_TotalFatura).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : ""}</td>}
                        {modal.source !== "fatura" && (
                          <td style={{ padding: "9px 16px", maxWidth: "340px", fontSize: 12 }}>
                            {(row.Inconsistencias || '').split('\n').filter(Boolean).map(function(line, idx, arr) {
                              return (
                                <div key={idx} title={line} style={idx < arr.length - 1 ? { borderBottom: `1px solid ${AUTOPILOT_COLORS.borderSoft}`, paddingBottom: "5px", marginBottom: "5px" } : {}}>
                                  {line}
                                </div>
                              );
                            })}
                          </td>
                        )}
                        <td style={{ padding: "8px 16px", whiteSpace: "nowrap" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                            {modal.source !== "fatura" && <button
                              onClick={function() { setXmlModal({ open: true, nomeArquivo: row.NomeArquivo, idTransportadora: row.IdTransportadora }); }}
                              style={{ background: "none", border: `1px solid ${AUTOPILOT_COLORS.infoButtonBorder}`, borderRadius: "3px", color: AUTOPILOT_COLORS.infoButtonTextSoft, fontSize: "0.68rem", padding: "1px 5px", cursor: "pointer", lineHeight: 1.4 }}
                            >XML</button>}
                            {modal.source !== "fatura" && <button
                              onClick={function() { handleDownload(row); }}
                              style={{ background: "none", border: `1px solid color-mix(in srgb, ${AUTOPILOT_COLORS.statusWarning} 40%, transparent)`, borderRadius: "3px", color: `color-mix(in srgb, ${AUTOPILOT_COLORS.statusWarning} 80%, transparent)`, fontSize: "0.68rem", padding: "1px 5px", cursor: "pointer", lineHeight: 1.4 }}
                            >Baixar</button>}
                            {modal.source === "fatura" && <button
                              onClick={function() { setTxtModal({ open: true, nomeArquivo: row.NomeArquivo, idTransportadora: row.IdTransportadora }); }}
                              style={{ background: "none", border: `1px solid ${AUTOPILOT_COLORS.infoButtonBorder}`, borderRadius: "3px", color: AUTOPILOT_COLORS.infoButtonTextSoft, fontSize: "0.68rem", padding: "1px 5px", cursor: "pointer", lineHeight: 1.4 }}
                            >TXT</button>}
                            {modal.source === "fatura" && <button
                              onClick={function() {
                                const dlParams = { nomeArquivo: row.NomeArquivo, idTransportadora: row.IdTransportadora, download: "1" };
                                if (empresaId) dlParams.embarcadorId = empresaId;
                                api.get("/autopilot/insights/fatura/txt", {
                                  params: dlParams,
                                  responseType: "blob",
                                }).then(function(res) {
                                  const url = URL.createObjectURL(new Blob([res.data], { type: "text/plain" }));
                                  const a = document.createElement("a");
                                  a.href = url; a.download = row.NomeArquivo; a.click();
                                  URL.revokeObjectURL(url);
                                }).catch(function(err) { console.error("[Autopilot] Erro ao baixar TXT:", err); });
                              }}
                              style={{ background: "none", border: `1px solid color-mix(in srgb, ${AUTOPILOT_COLORS.statusWarning} 40%, transparent)`, borderRadius: "3px", color: `color-mix(in srgb, ${AUTOPILOT_COLORS.statusWarning} 80%, transparent)`, fontSize: "0.68rem", padding: "1px 5px", cursor: "pointer", lineHeight: 1.4 }}
                            >Baixar</button>}
                            <button
                              onClick={function() {
                                if (modal.source === "fatura") {
                                  const cteCtx = row.cteBacklog && row.cteBacklog.length > 0
                                    ? "\n\n⚠️ Atenção: o(s) CTe(s) referenciado(s) nesta inconsistência JÁ ESTÃO no backlog de CTe com inconsistência própria:\n" +
                                      row.cteBacklog.map(function(c) { return "- CTe Nr. " + c.Numero + ": " + c.Inconsistencias; }).join('\n') +
                                      "\nA transportadora enviou os arquivos, mas os CTes ficaram presos por outro motivo — investigue o backlog de CTe antes de acionar a transportadora."
                                    : "";
                                  const msg = `Analise esta fatura com inconsistência de integração:\n- Número: ${row.Numero}\n- Transportadora: ${row.Fantasia}\n- Arquivo: ${row.NomeArquivo}\n- Valor: ${row.Val_TotalFatura ? Number(row.Val_TotalFatura).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "N/A"}\n- Inconsistência: "${row.Inconsistencias}"${cteCtx}\n\nO que causou esse problema e quais são os passos para resolver?`;
                                  setChatModal({ open: true, categoria: null, count: 0, initialMessage: msg, title: `Fatura ${row.Numero}`, preloadedAnswer: null, preloadedUserMessage: null });
                                  return;
                                }
                                const isNfeNaoEncontrada = row.Inconsistencias && row.Inconsistencias.indexOf('não encontrada no TMS') !== -1;
                                const isTagNota = row.Inconsistencias && (row.Inconsistencias.indexOf('Não foram encontradas Nota') !== -1 || row.Inconsistencias.indexOf('TAG de Notal Fiscal') !== -1);
                                const isCnpjRemetente = row.Inconsistencias && row.Inconsistencias.indexOf('CNPJ do Remetente') !== -1;
                                if (isNfeNaoEncontrada) {
                                  const chaveMatch = row.Inconsistencias && /\b([0-9]{44})\b/.exec(row.Inconsistencias);
                                  const chaveNFe = chaveMatch ? chaveMatch[1] : null;
                                  setDiagnosando({ nomeArquivo: row.NomeArquivo });
                                  api.post("/autopilot/diagnostico/nfe-nao-encontrada", {
                                    inconsistencia: row.Inconsistencias,
                                    chaveNFe,
                                    nomeArquivo: row.NomeArquivo,
                                    numero: row.Numero,
                                    datEmissao: row.Dat_Emissao,
                                    ...(empresaId ? { embarcadorId: empresaId } : {}),
                                  }).then(function(res) {
                                    setDiagnosando(null);
                                    setChatModal({ open: true, categoria: null, count: 0, initialMessage: null, title: `CTe ${row.Numero}`, preloadedAnswer: res.data.answer || null });
                                  }).catch(function() {
                                    setDiagnosando(null);
                                    const ctx = getInconsistenciaContext(row.Inconsistencias);
                                    const msg = `Analise este CTe:\n- Número: ${row.Numero}\n- Transportadora: ${row.Fantasia}\n- Inconsistência: "${row.Inconsistencias}"${ctx}\n\nO que causou esse problema e quais são os passos para resolver?`;
                                    setChatModal({ open: true, categoria: null, count: 0, initialMessage: msg, title: `CTe ${row.Numero}`, preloadedAnswer: null });
                                  });
                                } else if (isTagNota) {
                                  setDiagnosando({ nomeArquivo: row.NomeArquivo });
                                  api.post("/autopilot/diagnostico/tag-nota", {
                                    nomeArquivo: row.NomeArquivo,
                                    idTransportadora: row.IdTransportadora,
                                    numero: row.Numero,
                                    datEmissao: row.Dat_Emissao,
                                    ...(empresaId ? { embarcadorId: empresaId } : {}),
                                  }).then(function(res) {
                                    setDiagnosando(null);
                                    setChatModal({ open: true, categoria: null, count: 0, initialMessage: null, title: `CTe ${row.Numero}`, preloadedAnswer: res.data.answer || null });
                                  }).catch(function() {
                                    setDiagnosando(null);
                                    const msg = `Analise este CTe:\n- Número: ${row.Numero}\n- Transportadora: ${row.Fantasia}\n- Inconsistência: "${row.Inconsistencias}"\n\nO que causou esse problema e quais são os passos para resolver?`;
                                    setChatModal({ open: true, categoria: null, count: 0, initialMessage: msg, title: `CTe ${row.Numero}`, preloadedAnswer: null });
                                  });
                                } else if (isCnpjRemetente) {
                                  setDiagnosando({ nomeArquivo: row.NomeArquivo });
                                  api.post("/autopilot/diagnostico/cnpj-remetente", {
                                    nomeArquivo: row.NomeArquivo,
                                    idTransportadora: row.IdTransportadora,
                                    inconsistencia: row.Inconsistencias,
                                    numero: row.Numero,
                                    datEmissao: row.Dat_Emissao,
                                    ...(empresaId ? { embarcadorId: empresaId } : {}),
                                  }).then(function(res) {
                                    setDiagnosando(null);
                                    setChatModal({ open: true, categoria: null, count: 0, initialMessage: null, title: `CTe ${row.Numero}`, preloadedAnswer: res.data.answer || null });
                                  }).catch(function() {
                                    setDiagnosando(null);
                                    const msg = `Analise este CTe:\n- Número: ${row.Numero}\n- Transportadora: ${row.Fantasia}\n- Inconsistência: "${row.Inconsistencias}"\n\nO que causou esse problema e quais são os passos para resolver?`;
                                    setChatModal({ open: true, categoria: null, count: 0, initialMessage: msg, title: `CTe ${row.Numero}`, preloadedAnswer: null });
                                  });
                                } else if (row.Inconsistencias && row.Inconsistencias.indexOf('Divergência entre CNPJ do CTe') !== -1) {
                                  setDiagnosando({ nomeArquivo: row.NomeArquivo });
                                  api.get('/autopilot/insights/cte/nf-carriers', {
                                    params: { idWorkCte: row.idWorkCTe },
                                  }).then(function(resp) {
                                    setDiagnosando(null);
                                    const nfCarriers = resp.data || [];
                                    const cteCarrierName = row.Fantasia || 'desconhecida';
                                    const nfResumo = nfCarriers.map(function(nf) {
                                      const nome = nf.nfCarrierName || 'desconhecida';
                                      const cnpjDb = nf.nfCarrierCnpj;
                                      const cnpjChave = nf.nfChave && nf.nfChave.length === 44 ? nf.nfChave.slice(6, 20) : null;
                                      const cnpj = cnpjDb || (cnpjChave ? cnpjChave + ' (CNPJ emitente da NF)' : 'não identificado');
                                      return `  - NF ${nf.nfNumero}: transportadora "${nome}" (CNPJ: ${cnpj})`;
                                    }).join('\n');
                                    const msg = [
                                      `CTe ${row.Numero} apresenta divergência de transportadora:`,
                                      ``,
                                      `- Transportadora do CTe: "${cteCarrierName}"`,
                                      `- Transportadora(s) das Notas Fiscais associadas:`,
                                      nfResumo || '  (não identificadas)',
                                      ``,
                                      `O sistema bloqueou a integração porque a transportadora registrada no CTe é diferente da transportadora associada às notas fiscais no sistema.`,
                                      ``,
                                      `Preciso que você me ajude a identificar qual está correto:`,
                                      `1. Se o correto é o CTe → as notas fiscais estão vinculadas à transportadora errada no sistema. Pode ser necessário corrigir o cadastro ou o de-para de transportadoras.`,
                                      `2. Se o correto é o das Notas Fiscais → o CTe foi emitido pela transportadora errada. Nesse caso o usuário deve REJEITAR o CTe (botão "Rejeitar" disponível na tela) e solicitar à transportadora que reemita corretamente.`,
                                      ``,
                                      `Com base nas transportadoras envolvidas, qual é o cenário mais provável? Apresente também os próximos passos para cada caso.`,
                                    ].join('\n');
                                    setChatModal({ open: true, categoria: null, count: 0, initialMessage: msg, title: `CTe ${row.Numero}`, preloadedAnswer: null });
                                  }).catch(function() {
                                    setDiagnosando(null);
                                    const msg = `CTe ${row.Numero} (${row.Fantasia}) apresenta divergência entre a transportadora do CTe e a transportadora das notas fiscais associadas.\n\nO que causou esse problema e quais são os passos para resolver?`;
                                    setChatModal({ open: true, categoria: null, count: 0, initialMessage: msg, title: `CTe ${row.Numero}`, preloadedAnswer: null });
                                  });
                                } else {
                                  const ctx = getInconsistenciaContext(row.Inconsistencias);
                                  const msg = `Analise este CTe com inconsistência de integração:\n- Número: ${row.Numero}\n- Transportadora: ${row.Fantasia}\n- Arquivo: ${row.NomeArquivo}\n- Inconsistência: "${row.Inconsistencias}"${ctx}\n\nO que causou esse problema e quais são os passos para resolver?`;
                                  setChatModal({ open: true, categoria: null, count: 0, initialMessage: msg, title: `CTe ${row.Numero}`, preloadedAnswer: null });
                                }
                              }}
                              style={{ background: AUTOPILOT_COLORS.purpleBg, border: `1px solid ${AUTOPILOT_COLORS.purpleBorder}`, borderRadius: "3px", color: AUTOPILOT_COLORS.purpleText, fontSize: "0.68rem", padding: "1px 5px", cursor: diagnosando && diagnosando.nomeArquivo === row.NomeArquivo ? "not-allowed" : "pointer", lineHeight: 1.4, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "4px", minWidth: "66px" }}
                              disabled={!!(diagnosando && diagnosando.nomeArquivo === row.NomeArquivo)}
                              title="Analisar com Autopilot"
                            >
                              {diagnosando && diagnosando.nomeArquivo === row.NomeArquivo
                                ? "..."
                                : <><img src={autopilotMiniIcon} alt="" style={{ width: 12, height: 12, objectFit: "contain" }} />Autopilot</>}
                            </button>
                            {(modal.source === "fatura" || modal.source !== "fatura") && (
                              rejeitandoIndividual && rejeitandoIndividual.nomeArquivo === row.NomeArquivo && rejeitandoIndividual.loading ? (
                                <span style={{ color: AUTOPILOT_COLORS.textSofter, fontSize: "0.68rem" }}>Rejeitando...</span>
                              ) : (
                                <button
                                  onClick={function() {
                                    if (modal.source === "fatura") confirmarRejeitarIndividual(Object.assign({}, row, { _isFatura: true }));
                                    else confirmarRejeitarIndividual(row);
                                  }}
                                  style={{ background: "none", border: `1px solid color-mix(in srgb, ${AUTOPILOT_COLORS.statusDanger} 30%, transparent)`, borderRadius: "3px", color: `color-mix(in srgb, ${AUTOPILOT_COLORS.statusDanger} 70%, transparent)`, fontSize: "0.68rem", padding: "1px 5px", cursor: "pointer", lineHeight: 1.4 }}
                                >Rejeitar</button>
                              )
                            )}
                          </span>
                        </td>
                      </tr>
                      {modal.source === "fatura" && row.cteBacklog && row.cteBacklog.length > 0 && expandedCteRows.has(row.Id) && (
                        <tr style={{ background: "var(--everlog-card-surface-section)" }}>
                          <td colSpan={6} style={{ padding: "8px 16px 10px 32px", borderTop: "none", borderBottom: `1px solid ${AUTOPILOT_COLORS.border}` }}>
                            <div style={{ fontSize: 12, color: AUTOPILOT_COLORS.muted, marginBottom: 6, fontWeight: 600 }}>CTes no backlog com inconsistência:</div>
                            {row.cteBacklog.map(function(cte, ci) {
                              return (
                                <div key={ci} style={{ marginBottom: ci < row.cteBacklog.length - 1 ? 8 : 0, display: "flex", gap: 10, alignItems: "flex-start" }}>
                                  <span style={{ color: AUTOPILOT_COLORS.statusWarning, fontWeight: 600, whiteSpace: "nowrap", minWidth: 100 }}>Nr. {cte.Numero}</span>
                                  <span style={{ color: AUTOPILOT_COLORS.text, fontSize: 12, lineHeight: 1.4 }}>{cte.Inconsistencias}</span>
                                </div>
                              );
                            })}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ textAlign: "center", color: AUTOPILOT_COLORS.muted, padding: "32px" }}>Nenhum detalhe encontrado.</p>
          )}
        </ModalBody>
      </Modal>

    </Card>

      {rejeitarModal.open && ReactDOM.createPortal(
        <div style={{
          position: "fixed", inset: 0, zIndex: 10500,
          background: "var(--everlog-modal-backdrop)", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: "var(--everlog-card-surface)", border: `1px solid ${AUTOPILOT_COLORS.border}`, borderRadius: "10px",
            padding: "20px 24px", width: "360px", maxWidth: "90vw", boxShadow: "var(--everlog-card-shadow)",
          }}>
            <p style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, fontSize: "0.88rem", marginBottom: "6px" }}>Confirmar rejeição</p>
            <p style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem", marginBottom: "12px" }}>
              Adicione um comentário explicando o motivo (opcional, mas recomendado).
            </p>
            <textarea
              autoFocus
              value={rejeitarModal.comentario}
              onChange={function(e) { var val = e.target.value; setRejeitarModal(function(s) { return Object.assign({}, s, { comentario: val }); }); }}
              placeholder="Ex: Transportadora enviou XML sem protocolo SEFAZ. Solicitar reenvio."
              rows={3}
              style={{
                width: "100%", background: AUTOPILOT_COLORS.surfacePill, border: `1px solid ${AUTOPILOT_COLORS.borderFaint}`,
                borderRadius: "6px", color: AUTOPILOT_COLORS.text, fontSize: "0.78rem", padding: "8px 10px",
                resize: "none", outline: "none", fontFamily: "inherit", lineHeight: 1.5,
              }}
            />
            <div style={{ display: "flex", gap: "8px", marginTop: "14px", justifyContent: "flex-end" }}>
              <button
                onClick={function() { setRejeitarModal({ open: false, tipo: null, payload: null, comentario: "" }); }}
                style={{ background: AUTOPILOT_COLORS.borderSoft, border: "none", color: AUTOPILOT_COLORS.textSoft, borderRadius: "6px", padding: "6px 14px", fontSize: "0.8rem", cursor: "pointer" }}
              >Cancelar</button>
              <button
                onClick={function() { executarRejeicao(rejeitarModal.comentario); }}
                style={{ background: AUTOPILOT_COLORS.statusDanger, border: "none", color: AUTOPILOT_COLORS.onAccent, borderRadius: "6px", padding: "6px 16px", fontSize: "0.8rem", cursor: "pointer", fontWeight: 600 }}
              >Rejeitar</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </React.Fragment>
  );
};

export default InsightsCard;
